import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import {
  Users,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Home,
  Calendar,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Search,
  Filter,
  RefreshCw,
  X,
  Check,
  AlertCircle,
  AlertTriangle,
  Loader2,
  MessageSquare,
  UserPlus,
  ArrowRight,
  Clock,
  Building2,
  CreditCard,
  FileText,
  FileBarChart,
  ExternalLink,
  MoreHorizontal,
  Coins,
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  Lock,
  Unlock,
  Pencil,
  Trash2,
  Smile,
  Archive,
  RotateCcw,
  BarChart3,
  PieChart,
  Sparkles,
  Target,
  Trophy,
  Zap,
  Send,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
  Tag,
  Briefcase,
  CheckCircle,
  Info,
} from "lucide-react";
import AICallButton from "./AICallButton";
import {
  fetchLeads,
  fetchLeadStats,
  fetchLead,
  fetchLeadPool,
  createLead,
  unlockLead,
  updateLeadStatus,
  assignLead,
  addLeadNote,
  updateLeadNote,
  deleteLeadNote,
  toggleNoteReaction,
  convertLeadToCase,
  fetchArchivedLeads,
  archiveLead,
  restoreLead,
  fetchMarketplaceActivity,
  fetchTeamContext,
  fetchLeadsReports,
  fetchLeadsReport,
  generateLeadsReport,
  ARCHIVE_REASONS,
  STATUS_CONFIG,
  SOURCE_CONFIG,
  LEAD_STATUSES,
  getValidTransitions,
  formatLeadDate,
  formatPhoneNumber,
  formatCurrency,
  formatCurrencyAbbreviated,
  formatGrowthPercent,
} from "../services/LeadsService";
import MentionInput, { renderTextWithMentions } from "./MentionInput";
import SendToRealtorModal from "./SendToRealtorModal";
import SendToInsuranceModal from "./SendToInsuranceModal";
import SMSModal from "./SMSModal";

// Credit cost for lead access
const LEAD_ACCESS_CREDIT_COST = 15000;

// Quick reaction emojis for notes (like Slack)
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

// Sales-focused celebration messages
const SALES_CELEBRATION_MESSAGES = [
  "New Opportunity! 🎯",
  "Lead Secured! 💪",
  "Time to Close! 🔥",
  "Your Next Deal! 💰",
  "Go Get 'Em! 🚀",
  "Sales Machine! ⚡",
  "Pipeline Growing! 📈",
  "Winner Winner! 🏆",
  "Deal Hunter! 🎯",
  "Opportunity Knocks! 🚪",
  "Make It Happen! 💥",
  "Closer Mode! 😎",
  "Revenue Time! 💵",
  "Client Awaits! 🤝",
  "Seal the Deal! ✨",
  "Top Producer! 🌟",
  "Sales Superstar! ⭐",
  "Hustle Pays Off! 💎",
  "Next Level! 🔝",
  "You Got This! 👊",
  "Money Moves! 💸",
  "Commission Time! 🎉",
  "Customer First! ❤️",
  "Build Trust! 🤲",
  "Serve & Succeed! 🙌",
  "Dream Maker! 🏠",
  "Keys Incoming! 🔑",
  "Family Helper! 👨‍👩‍👧",
  "Life Changer! 🌈",
  "Trust Builder! 🤝",
  "Rate Crusher! 📉",
  "Loan Legend! 👑",
  "Mortgage Pro! 🎓",
  "Client Champion! 🏅",
  "Homerun Deal! ⚾",
  "Cash Flow King! 👑",
  "Quota Smasher! 💥",
  "Elite Closer! 🔒",
  "Hot Streak! 🔥",
  "On A Roll! 🎲",
  "Momentum! 🌊",
  "Crushing It! 💪",
  "Beast Mode! 🦁",
  "Unstoppable! 🚂",
  "Pure Gold! 🥇",
];

// Sales Celebration Hook
const useSalesCelebration = () => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [recycleConfetti, setRecycleConfetti] = useState(false);
  const [flyer, setFlyer] = useState(null);
  const [celebrationMessage, setCelebrationMessage] = useState(null);

  const triggerCelebration = useCallback(() => {
    // Reset states
    setShowConfetti(false);
    setFlyer(null);
    setCelebrationMessage(null);

    // Small delay to allow reset
    setTimeout(() => {
      setShowConfetti(true);
      setRecycleConfetti(true);
    }, 10);

    // Launch Flyer - sales themed emojis
    const { innerHeight } = window;
    const flyerTypes = ['💰', '🚀', '🎯', '💎', '🏆', '⭐', '🔥'];
    const flyerEmoji = flyerTypes[Math.floor(Math.random() * flyerTypes.length)];
    setFlyer({ emoji: flyerEmoji, y: Math.random() * (innerHeight * 0.5) + 200 });

    // Show motivational message
    const message = SALES_CELEBRATION_MESSAGES[Math.floor(Math.random() * SALES_CELEBRATION_MESSAGES.length)];
    setCelebrationMessage(message);

    // Stop Recycling
    setTimeout(() => setRecycleConfetti(false), 2500);

    // Cleanup
    setTimeout(() => {
      setShowConfetti(false);
      setFlyer(null);
      setCelebrationMessage(null);
    }, 5000);
  }, []);

  return {
    triggerCelebration,
    celebrationState: { showConfetti, recycleConfetti, flyer, celebrationMessage }
  };
};

// Sales Celebration Overlay Component
const SalesCelebrationOverlay = ({ state }) => {
  const { width, height } = useWindowSize();

  if (!state) return null;

  const { showConfetti, recycleConfetti, flyer, celebrationMessage } = state;

  if (!showConfetti && !flyer && !celebrationMessage) return null;

  return (
    <>
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
          <Confetti
            width={width}
            height={height}
            numberOfPieces={400}
            recycle={recycleConfetti}
            gravity={0.15}
            wind={0.01}
            colors={['#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4']}
          />
        </div>
      )}
      <AnimatePresence>
        {flyer && (
          <motion.div
            initial={{ x: -150, y: flyer.y, rotate: 10, scale: 0.5 }}
            animate={{ x: width + 150, rotate: -10, scale: 1.2 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 3.5, ease: "easeInOut" }}
            className="fixed z-[100] pointer-events-none text-6xl drop-shadow-2xl filter blur-[0.5px]"
          >
            {flyer.emoji}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {celebrationMessage && (
          <div className="fixed inset-0 flex items-center justify-center z-[100] pointer-events-none">
            <motion.div
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 1.5, opacity: 0, filter: 'blur(10px)' }}
              transition={{ type: "spring", bounce: 0.5 }}
              className="text-6xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-sky-500 to-violet-500 drop-shadow-[0_0_30px_rgba(16,185,129,0.5)] tracking-tighter text-center px-4"
            >
              {celebrationMessage}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

// Success Modal Component - Beautiful celebration modal for conversions and unlocks
const SuccessModal = ({ isOpen, onClose, type, data }) => {
  if (!isOpen) return null;

  const isConversion = type === "conversion";
  const isUnlock = type === "unlock";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 20 }}
        transition={{ type: "spring", bounce: 0.4 }}
        className="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Gradient Header */}
        <div className={`h-2 ${isConversion ? "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" : "bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500"}`} />

        {/* Content */}
        <div className="p-6 text-center">
          {/* Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", bounce: 0.5 }}
            className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
              isConversion
                ? "bg-gradient-to-br from-emerald-400 to-teal-500"
                : "bg-gradient-to-br from-violet-400 to-purple-500"
            }`}
          >
            {isConversion ? (
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
            )}
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-2"
          >
            {isConversion ? "Lead Converted!" : "Lead Unlocked!"}
          </motion.h2>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-slate-500 dark:text-slate-400 mb-6"
          >
            {isConversion
              ? "Your lead has been successfully converted to a case."
              : "You now have full access to this lead's information."}
          </motion.p>

          {/* Details Card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 mb-6 text-left"
          >
            {isConversion && data?.case_number && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500 dark:text-slate-400">Case Number</span>
                <span className="text-sm font-mono font-medium text-slate-900 dark:text-white">
                  {data.case_number}
                </span>
              </div>
            )}
            {isUnlock && data?.borrower_name && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-500 dark:text-slate-400">Name</span>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {data.borrower_name}
                  </span>
                </div>
                {data?.email && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Email</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                      {data.email}
                    </span>
                  </div>
                )}
                {data?.phone && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Phone</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                      {data.phone}
                    </span>
                  </div>
                )}
              </>
            )}
          </motion.div>

          {/* Motivational Message */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500 mb-6"
          >
            {isConversion ? "Time to close this deal! 🎯" : "Go make the connection! 🤝"}
          </motion.p>

          {/* Action Button */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            onClick={onClose}
            className={`w-full py-3 rounded-xl font-semibold text-white transition-all shadow-lg ${
              isConversion
                ? "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-emerald-500/25"
                : "bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 shadow-violet-500/25"
            }`}
          >
            {isConversion ? "View Case" : "View Lead Details"}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Stats Card Component
const StatsCard = ({ label, value, color, icon: Icon, isActive, onClick }) => {
  const colorClasses = {
    sky: "bg-sky-500",
    amber: "bg-amber-500",
    orange: "bg-orange-500",
    violet: "bg-violet-500",
    emerald: "bg-emerald-500",
    slate: "bg-slate-500",
    blue: "bg-blue-500",
  };

  return (
    <button
      onClick={onClick}
      className={`flex-1 min-w-[140px] p-4 rounded-xl border transition-all ${
        isActive
          ? "bg-slate-900 dark:bg-white border-slate-900 dark:border-white"
          : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="text-left">
          <p className={`text-2xl font-bold ${isActive ? "text-white dark:text-slate-900" : "text-slate-900 dark:text-white"}`}>
            {value}
          </p>
          <p className={`text-xs font-medium ${isActive ? "text-slate-300 dark:text-slate-600" : "text-slate-500 dark:text-slate-400"}`}>
            {label}
          </p>
        </div>
      </div>
    </button>
  );
};

// Status Badge Component
const StatusBadge = ({ status, size = "sm" }) => {
  // Normalize status to lowercase (backend may return uppercase like "NEW", "FOLLOW_UP")
  const normalizedStatus = (status || "new").toLowerCase();
  const config = STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.new;
  const sizeClasses = size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm";

  return (
    <span className={`inline-flex items-center rounded-full font-medium ${sizeClasses} ${config.bgClass} ${config.textClass}`}>
      {config.label}
    </span>
  );
};

// Source Badge Component
const SourceBadge = ({ source, size = "sm" }) => {
  // Normalize source: lowercase and remove common suffixes
  const normalizedSource = (source || "")
    .toLowerCase()
    .replace(/\.io$/, "")
    .replace(/\.us$/, "")
    .replace(/\.com$/, "");

  const config = SOURCE_CONFIG[normalizedSource] || SOURCE_CONFIG.unknown;
  const sizeClasses = size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm";

  return (
    <span className={`inline-flex items-center rounded-full font-medium ${sizeClasses} ${config.bgClass} ${config.textClass}`}>
      {config.label}
    </span>
  );
};

// Lead Row Component
const LeadRow = ({ lead, isSelected, onClick, hasAccess, userEmail }) => {
  // Check if this lead belongs to the current user
  const isOwnLead = lead.is_own_lead === true ||
    lead.assigned_to === userEmail ||
    lead.owner_email === userEmail ||
    (!lead.assigned_to && !lead.owner_email && lead.is_own_lead !== false);
  const ownerDisplay = isOwnLead ? "You" : (lead.owner_name || lead.assigned_to || "Team");

  return (
    <tr
      onClick={onClick}
      className={`cursor-pointer transition-colors ${
        isSelected
          ? "bg-sky-50 dark:bg-sky-900/20"
          : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
      }`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-medium text-sm ${
            hasAccess
              ? "bg-gradient-to-br from-sky-400 to-blue-500"
              : "bg-slate-400 dark:bg-slate-600"
          }`}>
            {hasAccess ? (lead.contact_name?.charAt(0)?.toUpperCase() || "?") : "?"}
          </div>
          <div>
            {hasAccess ? (
              <>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900 dark:text-white">{lead.contact_name || "Unknown"}</p>
                  {!isOwnLead && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                      Team
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">{lead.contact_email}</p>
              </>
            ) : (
              <>
                <p className="font-medium text-slate-500 dark:text-slate-400 italic">Contact Hidden</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 font-mono">{lead.lead_id}</p>
              </>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {hasAccess ? (
          <p className="text-sm text-slate-700 dark:text-slate-300">{formatPhoneNumber(lead.contact_phone)}</p>
        ) : (
          <p className="text-sm text-slate-400 dark:text-slate-500">•••••••••••</p>
        )}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={lead.status} />
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-slate-600 dark:text-slate-400">
          {ownerDisplay}
        </span>
      </td>
      <td className="px-4 py-3">
        <SourceBadge source={lead.source} />
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-slate-700 dark:text-slate-300">
          {formatCurrency(lead.estimated_value || lead.loan_details?.estimatedValue) || "-"}
        </p>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-slate-500 dark:text-slate-400">{formatLeadDate(lead.created_at)}</p>
      </td>
      <td className="px-4 py-3 text-right">
        <ChevronRight className="w-4 h-4 text-slate-400 inline" />
      </td>
    </tr>
  );
};

// Delete Note Confirmation Modal
const DeleteNoteModal = ({ isOpen, onClose, onConfirm, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={(e) => e.target === e.currentTarget && !isDeleting && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm overflow-hidden"
      >
        <div className="p-6">
          {/* Icon */}
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white text-center mb-2">
            Delete Note?
          </h3>

          {/* Message */}
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">
            This action cannot be undone.
          </p>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="flex-1 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Assign Lead Modal Component
const AssignLeadModal = ({ isOpen, onClose, onAssign, teamMembers, currentAssignee, isAssigning }) => {
  const [selectedMember, setSelectedMember] = useState(currentAssignee || "");
  const [sendNotification, setSendNotification] = useState(true);

  if (!isOpen) return null;

  const handleAssign = () => {
    if (!selectedMember) return;
    onAssign(selectedMember, sendNotification);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={(e) => e.target === e.currentTarget && !isAssigning && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm overflow-hidden"
      >
        <div className="p-6">
          {/* Icon */}
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
            <UserPlus className="w-6 h-6 text-sky-600 dark:text-sky-400" />
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white text-center mb-4">
            Assign Lead
          </h3>

          {/* Team Member Select */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Assign to
            </label>
            <select
              value={selectedMember}
              onChange={(e) => setSelectedMember(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <option value="">Select team member...</option>
              {teamMembers?.map((member) => (
                <option key={member.email} value={member.email}>
                  {member.name || member.email}
                </option>
              ))}
            </select>
          </div>

          {/* Send Notification Checkbox */}
          <label className="flex items-center gap-3 mb-6 cursor-pointer">
            <input
              type="checkbox"
              checked={sendNotification}
              onChange={(e) => setSendNotification(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-sky-600 focus:ring-sky-500"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Send email notification
            </span>
          </label>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isAssigning}
              className="flex-1 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedMember || isAssigning}
              className="flex-1 py-2.5 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isAssigning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                "Assign"
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Create Lead Modal Component
const CreateLeadModal = ({ isOpen, onClose, onCreate, isCreating }) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    loan_amount: "",
    property_value: "",
    loan_purpose: "purchase",
    property_type: "single_family",
    credit_score_range: "",
    employment_status: "employed",
    source: "web",
    notes: "",
  });
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // All contact fields are required
    if (!formData.first_name.trim()) {
      setError("First name is required");
      return;
    }

    if (!formData.last_name.trim()) {
      setError("Last name is required");
      return;
    }

    if (!formData.email.trim()) {
      setError("Email address is required");
      return;
    }

    if (!formData.phone.trim()) {
      setError("Phone number is required");
      return;
    }

    try {
      // Format data to match the structure from nhamy.us / rapidrates
      const contactName = [formData.first_name, formData.last_name].filter(Boolean).join(" ");
      const propertyAddress = [formData.address, formData.city, formData.state, formData.zip_code].filter(Boolean).join(", ");

      const leadPayload = {
        contact_name: contactName,
        contact_email: formData.email,
        contact_phone: formData.phone,
        source: formData.source,
        status: "new",
        loan_details: {
          loanPurpose: formData.loan_purpose,
          propertyType: formData.property_type,
          estimatedValue: formData.property_value ? parseFloat(formData.property_value.replace(/[^0-9.]/g, "")) : null,
          loanAmount: formData.loan_amount ? parseFloat(formData.loan_amount.replace(/[^0-9.]/g, "")) : null,
          creditScore: formData.credit_score_range,
          employmentStatus: formData.employment_status,
          propertyAddress: propertyAddress || null,
          additionalNotes: formData.notes || null,
        },
        // Also include flat fields for compatibility
        property_city: formData.city,
        property_state: formData.state,
        property_zip: formData.zip_code,
        property_type: formData.property_type,
        loan_purpose: formData.loan_purpose,
        estimated_value: formData.property_value ? parseFloat(formData.property_value.replace(/[^0-9.]/g, "")) : null,
      };

      await onCreate(leadPayload);
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        loan_amount: "",
        property_value: "",
        loan_purpose: "purchase",
        property_type: "single_family",
        credit_score_range: "",
        employment_status: "employed",
        source: "web",
        notes: "",
      });
      onClose();
    } catch (err) {
      setError(err.message || "Failed to create lead");
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Create New Lead</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Contact Information */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-sky-500" />
              Contact Information
              <span className="text-red-500 text-xs font-normal">* Required</span>
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="First Name *"
                required
                className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Last Name *"
                required
                className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email Address *"
                required
                className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone Number *"
                required
                className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Property Address */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-sky-500" />
              Property Address
            </h3>
            <div className="space-y-3">
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Street Address"
                className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="State"
                  className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
                <input
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  placeholder="ZIP Code"
                  className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Loan Details */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-sky-500" />
              Loan Details
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Loan Amount</label>
                <input
                  type="text"
                  name="loan_amount"
                  value={formData.loan_amount}
                  onChange={handleChange}
                  placeholder="$0"
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Property Value</label>
                <input
                  type="text"
                  name="property_value"
                  value={formData.property_value}
                  onChange={handleChange}
                  placeholder="$0"
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Loan Purpose</label>
                <select
                  name="loan_purpose"
                  value={formData.loan_purpose}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                >
                  <option value="purchase">Purchase</option>
                  <option value="refinance">Refinance</option>
                  <option value="cashout">Cash-Out Refinance</option>
                  <option value="heloc">HELOC</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Property Type</label>
                <select
                  name="property_type"
                  value={formData.property_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                >
                  <option value="single_family">Single Family</option>
                  <option value="condo">Condo</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="multi_family">Multi-Family</option>
                  <option value="manufactured">Manufactured</option>
                </select>
              </div>
            </div>
          </div>

          {/* Borrower Details */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-sky-500" />
              Borrower Details
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Credit Score Range</label>
                <select
                  name="credit_score_range"
                  value={formData.credit_score_range}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  <option value="excellent">Excellent (750+)</option>
                  <option value="good">Good (700-749)</option>
                  <option value="fair">Fair (650-699)</option>
                  <option value="poor">Poor (600-649)</option>
                  <option value="very_poor">Very Poor (&lt;600)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Employment Status</label>
                <select
                  name="employment_status"
                  value={formData.employment_status}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                >
                  <option value="employed">Employed</option>
                  <option value="self_employed">Self-Employed</option>
                  <option value="retired">Retired</option>
                  <option value="unemployed">Unemployed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Source */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Tag className="w-4 h-4 text-sky-500" />
              Lead Source
            </h3>
            <select
              name="source"
              value={formData.source}
              onChange={handleChange}
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
            >
              <option value="direct">Direct</option>
              <option value="referral">Referral</option>
              <option value="website">Website</option>
              <option value="social_media">Social Media</option>
              <option value="zillow">Zillow</option>
              <option value="realtor">Realtor.com</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-sky-500" />
              Notes
            </h3>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any notes about this lead..."
              rows={3}
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="px-6 py-2 bg-sky-600 text-white rounded-lg font-medium hover:bg-sky-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create Lead
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Lead Detail Panel Component
const LeadDetailPanel = ({ lead, onClose, onStatusChange, onAddNote, onUpdateNote, onDeleteNote, onToggleReaction, onAssign, onConvert, onArchive, onSendToRealtor, onSendToInsurance, teamMembers, userEmail, onStartCall, onSendSMS, hasAIDialerAccess, userCredits, onRefreshLead }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [newNote, setNewNote] = useState("");
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [isDeletingNote, setIsDeletingNote] = useState(null);
  const [pendingDeleteNoteIndex, setPendingDeleteNoteIndex] = useState(null);
  const [showQuickReactions, setShowQuickReactions] = useState(null); // noteIndex for showing reactions popup

  // Handle reaction toggle on a note
  const handleReaction = async (noteIndex, emoji) => {
    try {
      await onToggleReaction(lead.lead_id, noteIndex, emoji);
      setShowQuickReactions(null);
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
    }
  };

  // Check if note has actual content beyond just @mentions
  const hasActualNoteContent = (text) => {
    if (!text?.trim()) return false;
    // Strip out @mentions and check if there's remaining content
    const contentWithoutMentions = text.replace(/@\w+(\.\w+)*(@[\w.]+)?/g, '').trim();
    return contentWithoutMentions.length > 0;
  };

  // Check if current user can edit/delete a note
  const canEditNote = (note) => {
    // Don't allow editing system-generated notes
    if (note.is_system || note.system || note.type === 'system') return false;
    if (note.note?.startsWith('Assigned to ')) return false;

    // Only show buttons for notes created by current user
    const noteAuthor = note.author || note.created_by || note.updated_by;
    return noteAuthor?.toLowerCase() === userEmail?.toLowerCase();
  };

  const handleEditNote = async (noteIndex) => {
    if (!editingNoteText.trim()) return;
    setIsEditingNote(true);
    setNoteError(null);
    try {
      await onUpdateNote(lead.lead_id, noteIndex, editingNoteText);
      setEditingNoteIndex(null);
      setEditingNoteText("");
    } catch (err) {
      // Handle permission error gracefully
      setNoteError(err.message || "Failed to update note");
      setEditingNoteIndex(null);
      setEditingNoteText("");
    } finally {
      setIsEditingNote(false);
    }
  };

  const [noteError, setNoteError] = useState(null);

  const handleDeleteNote = async () => {
    if (pendingDeleteNoteIndex === null) return;
    setIsDeletingNote(pendingDeleteNoteIndex);
    setNoteError(null);
    try {
      await onDeleteNote(lead.lead_id, pendingDeleteNoteIndex);
      setPendingDeleteNoteIndex(null);
    } catch (err) {
      // Handle permission error gracefully
      setNoteError(err.message || "Failed to delete note");
      setPendingDeleteNoteIndex(null);
    } finally {
      setIsDeletingNote(null);
    }
  };

  const startEditingNote = (noteIndex, currentText) => {
    setEditingNoteIndex(noteIndex);
    setEditingNoteText(currentText);
  };

  const cancelEditingNote = () => {
    setEditingNoteIndex(null);
    setEditingNoteText("");
  };

  const handleStatusChange = async (newStatus) => {
    setIsUpdatingStatus(true);
    setShowStatusDropdown(false);
    try {
      await onStatusChange(lead.lead_id, newStatus);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    // Validate that note has actual content (not just @mentions)
    if (!hasActualNoteContent(newNote) && mentionedUsers.length > 0) {
      setNoteError("Please add a message along with the mention");
      return;
    }
    setIsAddingNote(true);
    setNoteError(null);
    try {
      await onAddNote(lead.lead_id, newNote, mentionedUsers);
      setNewNote("");
      setMentionedUsers([]);
    } catch (err) {
      setNoteError(err.message || "Failed to add note");
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleConvert = async () => {
    setIsConverting(true);
    try {
      await onConvert(lead.lead_id);
    } finally {
      setIsConverting(false);
    }
  };

  const handleAssign = async (email, sendNotification) => {
    setIsAssigning(true);
    try {
      await onAssign(lead.lead_id, email, sendNotification);
      setShowAssignModal(false);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="fixed right-0 top-0 h-full w-full max-w-lg bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg">
            {lead.contact_name?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div>
            <h2 className="font-bold text-lg text-slate-900 dark:text-white">{lead.contact_name}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Lead from {lead.source}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Quick Actions */}
      <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2">
        {/* Convert Button - Primary CTA */}
        {lead.status !== "converted" && (
          <button
            onClick={handleConvert}
            disabled={isConverting}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {isConverting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <ArrowRight className="w-4 h-4" />
                Convert to Case
              </>
            )}
          </button>
        )}

        {lead.status === "converted" && lead.converted_case_id && (
          <a
            href={`/cases/${lead.converted_case_id}`}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors"
          >
            <FileText className="w-4 h-4" />
            View Case
            <ExternalLink className="w-3 h-3" />
          </a>
        )}

        {/* Status Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            disabled={isUpdatingStatus}
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            {isUpdatingStatus ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <StatusBadge status={lead.status} />
            )}
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>
          {showStatusDropdown && (
            <>
              {/* Backdrop to close dropdown on click outside */}
              <div className="fixed inset-0 z-[5]" onClick={() => setShowStatusDropdown(false)} />
              <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-10 py-1">
                {/* Only show valid transitions based on current status */}
                {(() => {
                  const validTransitions = getValidTransitions(lead.status);
                  const options = Object.entries(LEAD_STATUSES)
                    .filter(([key, value]) => validTransitions.includes(value));

                  if (options.length === 0) {
                    return (
                      <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                        No status changes available
                      </div>
                    );
                  }

                  return options.map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => handleStatusChange(value)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                    >
                      <StatusBadge status={value} />
                    </button>
                  ));
                })()}
              </div>
            </>
          )}
        </div>

        {/* Assign Button */}
        <button
          onClick={() => setShowAssignModal(true)}
          className="flex items-center gap-2 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        >
          <UserPlus className="w-4 h-4 text-slate-500" />
          {lead.assigned_to ? lead.assigned_to.split("@")[0] : "Assign"}
        </button>

        {/* Send to Realtor Button */}
        {onSendToRealtor && (
          <button
            onClick={() => onSendToRealtor(lead)}
            className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-400 hover:from-purple-500/20 hover:to-pink-500/20 rounded-lg text-sm font-medium transition-all"
            title="Send to Realtor"
          >
            <Send className="w-4 h-4" />
            Send to Realtor
          </button>
        )}

        {/* Send to Insurance Button */}
        {onSendToInsurance && (
          <button
            onClick={() => onSendToInsurance(lead)}
            className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-400 hover:from-orange-500/20 hover:to-amber-500/20 rounded-lg text-sm font-medium transition-all"
            title="Send to Insurance Agent"
          >
            <Send className="w-4 h-4" />
            Send to Insurance
          </button>
        )}

        {/* Archive Button */}
        {lead.status !== "converted" && onArchive && (
          <button
            onClick={() => onArchive(lead)}
            className="flex items-center gap-2 px-3 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-sm font-medium transition-colors"
            title="Archive lead"
          >
            <Archive className="w-4 h-4" />
            Archive
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 dark:border-slate-800">
        {[
          { id: "details", label: "Details" },
          { id: "notes", label: "Notes", count: lead.notes?.length || 0 },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
              activeTab === tab.id
                ? "text-sky-600 dark:text-sky-400"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-slate-200 dark:bg-slate-700 rounded-full">
                {tab.count}
              </span>
            )}
            {activeTab === tab.id && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400"
              />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "details" && (
          <div className="p-6 space-y-6">
            {/* Contact Info */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <a href={`mailto:${lead.contact_email}`} className="text-sky-600 dark:text-sky-400 hover:underline">
                    {lead.contact_email}
                  </a>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <a href={`tel:${lead.contact_phone}`} className="text-sky-600 dark:text-sky-400 hover:underline">
                    {formatPhoneNumber(lead.contact_phone)}
                  </a>
                  {/*
                    Phone Dialer - Currently Ultra+ only
                    =====================================
                    UI TEAM NOTE: The phone dialer is temporarily restricted to Ultra+ users
                    because Amazon Connect authentication is not yet integrated for lower tiers.
                    The dialer doesn't work properly without proper Connect login/session setup.

                    TODO: Once Amazon Connect auth is implemented, update hasPhoneCalling
                    in subscriptionHelpers.js from 'ultra' back to 'pro' tier.
                  */}
                  {onStartCall && lead.contact_phone && (
                    <button
                      onClick={() => onStartCall({
                        phoneNumber: lead.contact_phone,
                        contactName: lead.contact_name,
                        leadId: lead.id
                      })}
                      className="ml-2 px-2 py-1 bg-green-500 hover:bg-green-600 text-white text-xs rounded-lg transition-colors flex items-center gap-1"
                      title="Start call"
                    >
                      <Phone className="w-3 h-3" />
                      Call
                    </button>
                  )}
                  {hasAIDialerAccess && lead.contact_phone && (
                    <AICallButton
                      lead={lead}
                      userCredits={userCredits}
                      onCallComplete={(result) => {
                        console.log('AI Call completed:', result);
                        // Refresh notes after call completes
                        if (onRefreshLead) onRefreshLead(lead.lead_id || lead.id);
                      }}
                    />
                  )}
                  {/* SMS Button - Hidden until 10DLC registration approved
                  {lead.contact_phone && onSendSMS && (
                    <button
                      onClick={() => onSendSMS(lead)}
                      className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-lg transition-colors flex items-center gap-1"
                      title="Send text message"
                    >
                      <MessageSquare className="w-3 h-3" />
                      Text
                    </button>
                  )}
                  */}
                </div>
                {lead.loan_details?.propertyAddress && (
                  <div className="flex items-start gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">{lead.loan_details.propertyAddress}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Mailing Address - For Off-Market Leads */}
            {lead.mailing_address && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  <Home className="w-4 h-4 text-violet-500" />
                  Mailing Address
                  {lead.loan_details?.isOffMarket && (
                    <span className="text-xs font-normal px-2 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-full">
                      Off-Market
                    </span>
                  )}
                </h3>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <div className="space-y-2">
                    {lead.mailing_address.name && (
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {lead.mailing_address.name}
                      </p>
                    )}
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      {lead.mailing_address.address_line1}
                    </p>
                    {lead.mailing_address.address_line2 && (
                      <p className="text-sm text-slate-700 dark:text-slate-300">
                        {lead.mailing_address.address_line2}
                      </p>
                    )}
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      {lead.mailing_address.address_city}, {lead.mailing_address.address_state} {lead.mailing_address.address_zip}
                    </p>
                  </div>
                  {/* Postcard indicator */}
                  {lead.postcard_history && lead.postcard_history.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                      <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
                        <CheckCircle className="w-3.5 h-3.5" />
                        <span>Postcard sent on {new Date(lead.postcard_history[0].sent_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Loan Details */}
            {lead.loan_details && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Loan Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  {lead.loan_details.loanPurpose && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Loan Purpose</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{lead.loan_details.loanPurpose}</p>
                    </div>
                  )}
                  {lead.loan_details.propertyType && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Property Type</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{lead.loan_details.propertyType}</p>
                    </div>
                  )}
                  {lead.loan_details.estimatedValue && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Estimated Value</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{formatCurrency(lead.loan_details.estimatedValue)}</p>
                    </div>
                  )}
                  {lead.loan_details.downPayment && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Down Payment</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{formatCurrency(lead.loan_details.downPayment)}</p>
                    </div>
                  )}
                  {lead.loan_details.creditScore && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Credit Score</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{lead.loan_details.creditScore}</p>
                    </div>
                  )}
                  {lead.loan_details.annualIncome && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Annual Income</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{formatCurrency(lead.loan_details.annualIncome)}</p>
                    </div>
                  )}
                  {lead.loan_details.occupancy && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Occupancy</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{lead.loan_details.occupancy}</p>
                    </div>
                  )}
                </div>
                {lead.loan_details.additionalNotes && (
                  <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Additional Notes</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{lead.loan_details.additionalNotes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Timeline Info */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Timeline</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Created</span>
                  <span className="text-slate-700 dark:text-slate-300">{formatLeadDate(lead.created_at)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Last Updated</span>
                  <span className="text-slate-700 dark:text-slate-300">{formatLeadDate(lead.updated_at)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Follow-ups</span>
                  <span className="text-slate-700 dark:text-slate-300">{lead.follow_up_count || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "notes" && (
          <div className="p-6">
            {/* Add Note Input with @mention support */}
            <div className="mb-4">
              <MentionInput
                value={newNote}
                onChange={setNewNote}
                onMentionsChange={setMentionedUsers}
                teamMembers={teamMembers}
                userEmail={userEmail}
                placeholder="Add a note... Type @ to mention team members"
                rows={3}
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {mentionedUsers.length > 0 && !hasActualNoteContent(newNote) ? (
                    <span className="text-amber-600 dark:text-amber-400">
                      Please add a message along with the mention
                    </span>
                  ) : mentionedUsers.length > 0 ? (
                    <span>
                      Mentioning: {mentionedUsers.map(u => u.name || u.email).join(', ')} (will be notified)
                    </span>
                  ) : null}
                </p>
                <button
                  onClick={handleAddNote}
                  disabled={!newNote.trim() || isAddingNote || (mentionedUsers.length > 0 && !hasActualNoteContent(newNote))}
                  className="px-4 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium hover:bg-sky-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isAddingNote ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <MessageSquare className="w-4 h-4" />
                  )}
                  Add Note
                </button>
              </div>
            </div>

            {/* Note Error Display */}
            {noteError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {noteError}
                  </div>
                  <button onClick={() => setNoteError(null)} className="text-red-400 hover:text-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Notes Timeline - pr-16 to avoid floating buttons */}
            <div className="space-y-4 pr-16">
              {lead.notes?.length > 0 ? (
                lead.notes.map((note, idx) => (
                  <div key={idx} className="relative pl-6 pb-4 border-l-2 border-slate-200 dark:border-slate-700 last:pb-0">
                    <div className="absolute left-[-5px] top-0 w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" />
                    <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                      {editingNoteIndex === note.index ? (
                        /* Editing Mode */
                        <div>
                          <textarea
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none text-sm"
                            autoFocus
                          />
                          <div className="flex justify-end gap-2 mt-2">
                            <button
                              onClick={cancelEditingNote}
                              disabled={isEditingNote}
                              className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleEditNote(note.index)}
                              disabled={!editingNoteText.trim() || isEditingNote}
                              className="px-3 py-1.5 text-sm bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                              {isEditingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* Display Mode - with @mention highlighting */
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm text-slate-700 dark:text-slate-300 flex-1 whitespace-pre-wrap">{renderTextWithMentions(note.note, teamMembers)}</p>
                            {canEditNote(note) && (
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  onClick={() => startEditingNote(note.index, note.note)}
                                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                  title="Edit note"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => setPendingDeleteNoteIndex(note.index)}
                                  disabled={isDeletingNote === note.index}
                                  className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                  title="Delete note"
                                >
                                  {isDeletingNote === note.index ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
                            <span>{note.author || note.created_by || note.updated_by}</span>
                            <span>•</span>
                            <span>{formatLeadDate(note.timestamp)}</span>
                            {note.edited_at && (
                              <>
                                <span>•</span>
                                <span className="text-slate-400 italic">(edited)</span>
                              </>
                            )}
                            {note.status_change && (
                              <>
                                <span>•</span>
                                <StatusBadge status={note.status_change} size="sm" />
                              </>
                            )}
                          </div>

                          {/* Reactions Section */}
                          <div className="flex items-center gap-2 mt-2 relative">
                            {/* Existing Reactions */}
                            {note.reactions && note.reactions.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {note.reactions.map((reaction) => (
                                  <button
                                    key={reaction.emoji}
                                    onClick={() => handleReaction(note.index, reaction.emoji)}
                                    className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 transition ${
                                      reaction.userReacted
                                        ? 'bg-sky-600/30 border border-sky-600/50 text-sky-700 dark:text-white'
                                        : 'dark:bg-slate-700/80 bg-slate-100 border dark:border-slate-600/50 border-slate-200 dark:text-slate-300 text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600'
                                    }`}
                                    title={reaction.users?.join(', ') || ''}
                                  >
                                    <span>{reaction.emoji}</span>
                                    <span className="font-medium">{reaction.count}</span>
                                  </button>
                                ))}
                              </div>
                            )}

                            {/* Add Reaction Button */}
                            <button
                              onClick={() => setShowQuickReactions(showQuickReactions === note.index ? null : note.index)}
                              className="p-1 text-slate-400 hover:text-yellow-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                              title="Add reaction"
                            >
                              <Smile className="w-4 h-4" />
                            </button>

                            {/* Quick Reactions Popup */}
                            <AnimatePresence>
                              {showQuickReactions === note.index && (
                                <>
                                  {/* Backdrop to close on click outside */}
                                  <div
                                    className="fixed inset-0 z-40"
                                    onClick={() => setShowQuickReactions(null)}
                                  />
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.95, y: -5 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: -5 }}
                                    className="absolute left-0 bottom-full mb-1 dark:bg-slate-800 bg-white rounded-xl shadow-2xl border dark:border-slate-700 border-slate-200 p-2 flex gap-1 z-50"
                                  >
                                    {QUICK_REACTIONS.map(emoji => (
                                      <button
                                        key={emoji}
                                        onClick={() => handleReaction(note.index, emoji)}
                                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-all hover:scale-125"
                                      >
                                        <span className="text-xl">{emoji}</span>
                                      </button>
                                    ))}
                                  </motion.div>
                                </>
                              )}
                            </AnimatePresence>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 dark:text-slate-400 py-8">No notes yet</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Note Confirmation Modal */}
      <AnimatePresence>
        {pendingDeleteNoteIndex !== null && (
          <DeleteNoteModal
            isOpen={pendingDeleteNoteIndex !== null}
            onClose={() => setPendingDeleteNoteIndex(null)}
            onConfirm={handleDeleteNote}
            isDeleting={isDeletingNote !== null}
          />
        )}
      </AnimatePresence>

      {/* Assign Lead Modal */}
      <AnimatePresence>
        {showAssignModal && (
          <AssignLeadModal
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            onAssign={handleAssign}
            teamMembers={teamMembers}
            currentAssignee={lead.assigned_to}
            isAssigning={isAssigning}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Unlock Modal Component - Requires Access Code + Credits
const UnlockModal = ({ lead, userCredits, creditCost, isUnlocking, onClose, onUnlock }) => {
  const [accessCode, setAccessCode] = useState("");
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
    setAccessCode(value);
    setError(null);
  };

  const handleSubmit = () => {
    if (!accessCode.trim() || accessCode.length !== 8) {
      setError("Please enter a valid 8-character access code");
      return;
    }
    if (userCredits < creditCost) {
      setError("Insufficient credits");
      return;
    }
    onUnlock(lead, accessCode);
  };

  const hasEnoughCredits = userCredits >= creditCost;
  const canUnlock = accessCode.length === 8 && hasEnoughCredits && !isUnlocking;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-violet-500 to-purple-500">
          <h2 className="text-lg font-bold text-white">Unlock This Lead</h2>
        </div>

        <div className="p-6">
          {/* Lead Preview */}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <SourceBadge source={lead.source} />
              <span className="text-xs font-mono text-slate-400">{lead.lead_id}</span>
            </div>
            <p className="text-xl font-bold text-slate-900 dark:text-white mb-1">
              {formatCurrency(lead.estimated_value) || lead.estimated_value_range || "N/A"} • {lead.loan_purpose || "Loan"}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {[lead.property_city, lead.property_state].filter(Boolean).join(", ") || "Location not specified"}
            </p>
            {/* Additional info */}
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              {lead.property_type && (
                <span className="px-2 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">{lead.property_type}</span>
              )}
              {(lead.credit_score || lead.credit_score_range) && (
                <span className="px-2 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">Credit: {lead.credit_score || lead.credit_score_range}</span>
              )}
              {(lead.down_payment || lead.down_payment_range) && (
                <span className="px-2 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">Down: {lead.down_payment ? formatCurrency(lead.down_payment) : lead.down_payment_range}</span>
              )}
            </div>
          </div>

          {/* Access Code Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Access Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={accessCode}
              onChange={handleInputChange}
              placeholder="Enter 8-character code"
              className="w-full px-4 py-3 text-center text-xl font-mono tracking-widest border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 uppercase"
              autoFocus
              autoComplete="off"
            />
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              Check your email notification for the access code
            </p>
          </div>

          {/* Cost Info */}
          <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl mb-2">
            <div className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Unlock Cost</span>
            </div>
            <span className="font-bold text-amber-700 dark:text-amber-400">
              {creditCost.toLocaleString()} credits
            </span>
          </div>

          {/* Balance Info */}
          <div className="flex items-center justify-between text-sm mb-4">
            <span className="text-slate-500 dark:text-slate-400">Your Balance</span>
            <span className={`font-medium ${hasEnoughCredits ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
              {userCredits.toLocaleString()} credits
            </span>
          </div>

          {/* Error Message */}
          {(error || !hasEnoughCredits) && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error || `Insufficient credits. You need ${(creditCost - userCredits).toLocaleString()} more.`}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canUnlock}
              className="flex-1 py-3 bg-violet-600 hover:bg-violet-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white disabled:text-slate-500 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
            >
              {isUnlocking ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Unlocking...
                </>
              ) : (
                <>
                  <Unlock className="w-4 h-4" />
                  Unlock Lead
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Report Detail View Component - Sidebar Layout like AI Underwriting Report
const ReportDetailView = ({ reportData, teamContext, onBack }) => {
  const [activeSection, setActiveSection] = useState('summary');

  // Define report sections based on available data
  const sections = [
    { id: 'summary', label: 'Executive Summary', icon: FileText, available: !!reportData.analysis?.executive_summary },
    { id: 'pipeline', label: 'Pipeline Overview', icon: BarChart3, available: !!reportData.pipeline_summary },
    { id: 'activity', label: '7-Day Activity', icon: Calendar, available: !!reportData.activity_last_7_days },
    { id: 'attention', label: 'Attention Needed', icon: AlertTriangle, available: reportData.analysis?.attention_needed?.length > 0 },
    { id: 'actions', label: 'Recommended Actions', icon: Target, available: reportData.analysis?.recommended_actions?.length > 0 },
    { id: 'insights', label: 'Key Insights', icon: Zap, available: reportData.analysis?.key_insights?.length > 0 },
    { id: 'wins', label: 'Wins This Week', icon: Trophy, available: reportData.analysis?.wins_today?.length > 0 },
    { id: 'conversion', label: 'Conversion Metrics', icon: TrendingUp, available: !!reportData.conversion_metrics },
  ].filter(s => s.available);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(`report-section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="flex h-full">
      {/* Left Sidebar */}
      <div className="w-64 bg-slate-900 dark:bg-slate-950 flex-shrink-0 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-2 text-white">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="font-semibold">AI Daily Report</span>
          </div>
        </div>

        {/* Sections Navigation */}
        <div className="p-3 flex-1 overflow-y-auto">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 mb-2">
            Sections
          </p>
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeSection === section.id
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <section.icon className="w-4 h-4" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Back Button */}
        <div className="p-4 border-t border-slate-800">
          <button
            onClick={onBack}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Reports
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-slate-100 dark:bg-slate-900/50 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Executive Summary Section */}
          {reportData.analysis?.executive_summary && (
            <section id="report-section-summary" className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                  Executive Summary
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  Report Date: {new Date(reportData.report_date + 'T00:00:00').toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })} • {teamContext?.team_owner_id || 'Your Team'}
                </p>
                <div className="prose dark:prose-invert max-w-none">
                  <p className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">
                    {reportData.analysis.executive_summary}
                  </p>
                </div>
              </div>
            </section>
          )}

          {/* Pipeline Overview Section */}
          {reportData.pipeline_summary && (
            <section id="report-section-pipeline" className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
                  Pipeline Overview
                </h2>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">
                      {reportData.pipeline_summary.total_active_leads ?? 0}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Active Leads</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                      {reportData.pipeline_summary.total_pipeline_value ?? '$0'}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Pipeline Value</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                      {reportData.activity_last_7_days?.leads_contacted ?? 0}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Contacted (7D)</p>
                  </div>
                </div>
                {reportData.pipeline_summary.by_stage && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">By Stage</h3>
                    <div className="space-y-2">
                      {Object.entries(reportData.pipeline_summary.by_stage).map(([stage, count]) => (
                        <div key={stage} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-700 last:border-0">
                          <span className="text-slate-600 dark:text-slate-400 capitalize">{stage.toLowerCase()}</span>
                          <span className="font-semibold text-slate-900 dark:text-white">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 7-Day Activity Section */}
          {reportData.activity_last_7_days && (
            <section id="report-section-activity" className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
                  7-Day Activity
                </h2>
                <div className="grid grid-cols-4 gap-4">
                  {[
                    { label: 'New Leads Added', value: reportData.activity_last_7_days.new_leads_added, color: 'sky' },
                    { label: 'Leads Contacted', value: reportData.activity_last_7_days.leads_contacted, color: 'amber' },
                    { label: 'Leads Qualified', value: reportData.activity_last_7_days.leads_qualified, color: 'violet' },
                    { label: 'Leads Converted', value: reportData.activity_last_7_days.leads_converted, color: 'emerald' },
                    { label: 'Leads Funded', value: reportData.activity_last_7_days.leads_funded, color: 'green' },
                    { label: 'Leads Lost', value: reportData.activity_last_7_days.leads_lost, color: 'slate' },
                    { label: 'Notes Added', value: reportData.activity_last_7_days.notes_added, color: 'blue' },
                  ].map((item) => (
                    <div key={item.label} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className={`text-2xl font-bold text-${item.color}-600 dark:text-${item.color}-400`}>
                        {item.value ?? 0}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Attention Needed Section */}
          {reportData.analysis?.attention_needed?.length > 0 && (
            <section id="report-section-attention" className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Attention Needed
                </h2>
                <div className="space-y-4">
                  {reportData.analysis.attention_needed.map((item, idx) => (
                    <div key={idx} className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-lg p-4">
                      <p className="font-semibold text-slate-900 dark:text-white">{item.borrower}</p>
                      <p className="text-slate-600 dark:text-slate-400 mt-1">{item.reason}</p>
                      <p className="text-amber-700 dark:text-amber-400 mt-2 text-sm">
                        <span className="font-semibold">Action:</span> {item.action}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Recommended Actions Section */}
          {reportData.analysis?.recommended_actions?.length > 0 && (
            <section id="report-section-actions" className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-500" />
                  Tomorrow's Top Actions
                </h2>
                <div className="space-y-3">
                  {reportData.analysis.recommended_actions.map((action, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </span>
                      <p className="text-slate-700 dark:text-slate-300 pt-1">{action}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Key Insights Section */}
          {reportData.analysis?.key_insights?.length > 0 && (
            <section id="report-section-insights" className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                  Key Insights
                </h2>
                <ul className="space-y-3">
                  {reportData.analysis.key_insights.map((insight, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                      <span className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                      <span>{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Wins This Week Section */}
          {reportData.analysis?.wins_today?.length > 0 && (
            <section id="report-section-wins" className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-emerald-500" />
                  Wins This Week
                </h2>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
                  <ul className="space-y-2">
                    {reportData.analysis.wins_today.map((win, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-emerald-800 dark:text-emerald-300">
                        <span className="mt-0.5">🎉</span>
                        <span>{win}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
          )}

          {/* Conversion Metrics Section */}
          {reportData.conversion_metrics && (
            <section id="report-section-conversion" className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  Conversion Metrics
                </h2>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-sky-600 dark:text-sky-400">
                      {reportData.conversion_metrics.new_to_contacted_rate}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">New → Contacted</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                      {reportData.conversion_metrics.contacted_to_qualified_rate}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Contacted → Qualified</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                      {reportData.conversion_metrics.qualified_to_funded_rate}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Qualified → Funded</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center py-2">
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">
                      {reportData.conversion_metrics.total_journeys}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Total Journeys</p>
                  </div>
                  <div className="text-center py-2">
                    <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                      {reportData.conversion_metrics.total_funded}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Total Funded</p>
                  </div>
                  <div className="text-center py-2">
                    <p className="text-lg font-semibold text-slate-500 dark:text-slate-400">
                      {reportData.conversion_metrics.total_lost}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Total Lost</p>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Leads Dashboard Component
const LeadsDashboard = ({
  userEmail,
  userMeta,
  teamMembers = [],
  onCreditsUpdate,
  userCredits = 0,
  featureFlags = {},
  onStartCall
}) => {
  // Check if user has marketplace access (Business+ tiers)
  const hasMarketplaceAccess = featureFlags.hasLeadsMarketplace === true;

  // Check if user has phone calling access (Pro+ tiers)
  const hasPhoneCallingAccess = featureFlags.hasPhoneCalling === true;

  // Check if user has AI Dialer access (Ultra+ tiers only)
  const hasAIDialerAccess = featureFlags.isUltraOrEnterprise === true;

  // Team context state - fetched from API on mount
  const [teamContext, setTeamContext] = useState(null);
  const [teamContextLoading, setTeamContextLoading] = useState(true);

  // Effective team ID for reports (use team_owner_id from context, fallback to userEmail)
  const effectiveTeamId = teamContext?.team_owner_id || userEmail;

  // Tab state: "marketplace" or "myleads"
  const [activeTab, setActiveTab] = useState("myleads");

  // My Leads state (user's unlocked leads)
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);

  // Compute stats from actual leads array to ensure accuracy
  // (Backend stats API may have inconsistencies with filtered notes/assignments)
  const computedStats = useMemo(() => {
    if (!leads || leads.length === 0) return stats; // Fall back to backend stats if no leads loaded

    // Filter out archived leads for stats calculation
    const activeLeads = leads.filter(lead => (lead.status || "").toLowerCase() !== "archive");

    const statusCounts = activeLeads.reduce((acc, lead) => {
      const status = (lead.status || 'new').toLowerCase();
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return {
      total: activeLeads.length,
      new: statusCounts.new || 0,
      contacted: statusCounts.contacted || 0,
      processing: statusCounts.processing || 0,
      follow_up: statusCounts.follow_up || 0,
      qualified: statusCounts.qualified || 0,
      converted: statusCounts.converted || 0,
      lost: statusCounts.lost || 0,
    };
  }, [leads, stats]);

  // Marketplace state (all available leads - preview only)
  const [poolLeads, setPoolLeads] = useState([]);
  const [poolLoading, setPoolLoading] = useState(false);
  const [marketplaceSearch, setMarketplaceSearch] = useState("");
  const [marketplaceSubTab, setMarketplaceSubTab] = useState("available"); // "available" | "report"

  // Marketplace Activity/Report state
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  // Archive state
  const [archivedLeads, setArchivedLeads] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archiveSearch, setArchiveSearch] = useState("");
  const [pendingArchiveLead, setPendingArchiveLead] = useState(null);
  const [archiveReasonSelect, setArchiveReasonSelect] = useState("");
  const [archiveReasonCustom, setArchiveReasonCustom] = useState("");
  const [isArchiving, setIsArchiving] = useState(false);
  const [pendingRestoreLead, setPendingRestoreLead] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);

  // Daily Reports state
  const [dailyReports, setDailyReports] = useState([]);
  const [dailyReportsLoading, setDailyReportsLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedReportData, setSelectedReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);

  // Time range filter for Daily Reports
  const [reportTimeRange, setReportTimeRange] = useState("daily"); // "daily" | "weekly" | "monthly" | "custom"
  const [customDateRange, setCustomDateRange] = useState({ start: "", end: "" });
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [ownershipFilter, setOwnershipFilter] = useState("all"); // "all" | "mine" | "team"
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("created_at"); // contact, phone, status, owner, source, value, created_at
  const [sortDirection, setSortDirection] = useState("desc"); // asc or desc
  const [pendingUnlockLead, setPendingUnlockLead] = useState(null); // Lead to unlock from marketplace
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isRefreshingCredits, setIsRefreshingCredits] = useState(false);

  // Success modal state
  const [successModal, setSuccessModal] = useState({ isOpen: false, type: null, data: null });

  // Create Lead modal state
  const [showCreateLeadModal, setShowCreateLeadModal] = useState(false);
  const [isCreatingLead, setIsCreatingLead] = useState(false);

  // Send to Realtor modal state
  const [sendToRealtorModal, setSendToRealtorModal] = useState({ isOpen: false, lead: null });

  // Send to Insurance modal state
  const [sendToInsuranceModal, setSendToInsuranceModal] = useState({ isOpen: false, lead: null });

  // SMS modal state
  const [smsModal, setSmsModal] = useState({ isOpen: false, contact: null });

  // Check if user has Ultra+ access for Send to Realtor feature
  const hasUltraAccess = featureFlags.hasSendToRealtor === true || featureFlags.tier === "ultra" || featureFlags.tier === "enterprise";

  // Sales celebration
  const { triggerCelebration, celebrationState } = useSalesCelebration();

  // Calculate total pipeline value from leads
  const pipelineStats = React.useMemo(() => {
    // Helper to safely parse value as number
    const parseValue = (lead) => {
      const val = lead?.loan_details?.estimatedValue;
      if (!val) return 0;
      const parsed = parseFloat(String(val).replace(/[^0-9.-]/g, ""));
      return isNaN(parsed) ? 0 : parsed;
    };

    const activeStatuses = ["new", "contacted", "follow_up", "qualified"];
    const activePipeline = leads.filter((lead) => activeStatuses.includes(lead.status));
    const totalValue = leads.reduce((sum, lead) => sum + parseValue(lead), 0);
    const activeValue = activePipeline.reduce((sum, lead) => sum + parseValue(lead), 0);

    // Breakdown by status
    const byStatus = {
      new: leads.filter((l) => l.status === "new").reduce((sum, l) => sum + parseValue(l), 0),
      contacted: leads.filter((l) => l.status === "contacted").reduce((sum, l) => sum + parseValue(l), 0),
      follow_up: leads.filter((l) => l.status === "follow_up").reduce((sum, l) => sum + parseValue(l), 0),
      qualified: leads.filter((l) => l.status === "qualified").reduce((sum, l) => sum + parseValue(l), 0),
    };

    return { totalValue, activeValue, byStatus, activeCount: activePipeline.length };
  }, [leads]);

  // Handle credits refresh
  const handleRefreshCredits = async () => {
    if (isRefreshingCredits || !onCreditsUpdate) return;
    setIsRefreshingCredits(true);
    try {
      await onCreditsUpdate();
    } finally {
      setIsRefreshingCredits(false);
    }
  };

  // Load marketplace leads
  const loadMarketplace = useCallback(async () => {
    setPoolLoading(true);
    try {
      const data = await fetchLeadPool({ limit: 100 });
      setPoolLeads(data.leads || []);
    } catch (err) {
      console.error("Failed to load marketplace:", err);
    } finally {
      setPoolLoading(false);
    }
  }, []);

  // Load team context to get team_owner_id for reports
  const loadTeamContext = useCallback(async () => {
    setTeamContextLoading(true);
    try {
      const response = await fetchTeamContext();
      console.log("Team Context API Response:", response);
      if (response.success && response.data) {
        setTeamContext(response.data);
      }
    } catch (err) {
      console.error("Failed to load team context:", err);
      // Fallback to userEmail if team context fails
    } finally {
      setTeamContextLoading(false);
    }
  }, []);

  // Load daily reports list
  // Note: API automatically resolves team based on authenticated user
  const loadDailyReports = useCallback(async () => {
    setDailyReportsLoading(true);
    try {
      const response = await fetchLeadsReports({ limit: 30 });
      console.log("Daily Reports API Response:", response);
      setDailyReports(response.data?.reports || []);
    } catch (err) {
      console.error("Failed to load daily reports:", err);
    } finally {
      setDailyReportsLoading(false);
    }
  }, []);

  // Load a specific daily report
  // Note: API automatically resolves team based on authenticated user
  const loadReportDetail = useCallback(async (reportDate) => {
    setReportLoading(true);
    setSelectedReport(reportDate);
    try {
      const response = await fetchLeadsReport(reportDate);
      console.log("Report Detail API Response:", response);
      // API returns { success: true, data: {...} } - extract the data
      const reportData = response?.data || response;
      setSelectedReportData(reportData);
    } catch (err) {
      console.error("Failed to load report detail:", err);
      setSelectedReportData(null);
    } finally {
      setReportLoading(false);
    }
  }, []);

  // Generate a new daily report
  // Note: API automatically resolves team based on authenticated user
  const handleGenerateReport = useCallback(async (force = false) => {
    setGeneratingReport(true);
    try {
      const response = await generateLeadsReport(force);
      console.log("Generate Report API Response:", response);
      // Refresh the reports list after generating
      await loadDailyReports();
      // Optionally select the newly generated report
      if (response.data?.report_date) {
        loadReportDetail(response.data.report_date);
      }
    } catch (err) {
      console.error("Failed to generate report:", err);
      // Could show error toast here
      alert(err.message || "Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  }, [loadDailyReports, loadReportDetail]);

  // Filter reports based on time range
  const filteredReports = React.useMemo(() => {
    if (!dailyReports.length) return [];

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return dailyReports.filter((report) => {
      const reportDate = new Date(report.report_date + 'T00:00:00');

      switch (reportTimeRange) {
        case "daily":
          // Show last 7 days
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          return reportDate >= sevenDaysAgo;

        case "weekly":
          // Show last 4 weeks (28 days)
          const fourWeeksAgo = new Date(today);
          fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
          return reportDate >= fourWeeksAgo;

        case "monthly":
          // Show last 3 months (90 days)
          const threeMonthsAgo = new Date(today);
          threeMonthsAgo.setDate(threeMonthsAgo.getDate() - 90);
          return reportDate >= threeMonthsAgo;

        case "custom":
          if (customDateRange.start && customDateRange.end) {
            const startDate = new Date(customDateRange.start + 'T00:00:00');
            const endDate = new Date(customDateRange.end + 'T23:59:59');
            return reportDate >= startDate && reportDate <= endDate;
          }
          return true; // Show all if custom range not set

        default:
          return true;
      }
    });
  }, [dailyReports, reportTimeRange, customDateRange]);

  // Load marketplace activity/report data
  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const data = await fetchMarketplaceActivity();
      console.log("Marketplace Activity API Response:", data);

      // Transform API response to match UI expectations
      // API returns: { today: {...}, this_week: {...}, this_month: {...}, all_time: {...} }
      // UI expects: { summary: { today: {...}, ... }, by_source: [...], by_state: [...] }
      const transformedData = {
        summary: {
          today: {
            count: data.today?.count ?? 0,
            pipeline_value: data.today?.total_loan_value ?? 0,
            growth: data.today?.vs_last_week ?? undefined,
          },
          this_week: {
            count: data.this_week?.count ?? 0,
            pipeline_value: data.this_week?.total_loan_value ?? 0,
            growth: data.this_week?.vs_last_week ?? undefined,
          },
          this_month: {
            count: data.this_month?.count ?? 0,
            pipeline_value: data.this_month?.total_loan_value ?? 0,
            growth: data.this_month?.vs_last_month ?? undefined,
          },
          all_time: {
            count: data.all_time?.count ?? 0,
            pipeline_value: data.all_time?.total_loan_value ?? 0,
            avg_loan_value: data.all_time?.avg_loan_value ?? 0,
          },
        },
        // Convert by_source object to array: {"nhamy": 14} -> [{source: "nhamy", count: 14}]
        by_source: Object.entries(data.all_time?.by_source || {}).map(([source, count]) => ({
          source,
          count,
        })).sort((a, b) => b.count - a.count),
        // Convert by_state object to array and aggregate by state code
        // Backend now returns proper 2-letter state abbreviations via property_state field
        by_state: (() => {
          // Map common zip prefixes to state codes for backwards compatibility with legacy data
          const ZIP_PREFIX_TO_STATE = {
            '75': 'TX', '77': 'TX', '76': 'TX', '78': 'TX', '79': 'TX',
            '32': 'FL', '33': 'FL', '34': 'FL',
            '90': 'CA', '91': 'CA', '92': 'CA', '93': 'CA', '94': 'CA', '95': 'CA',
            '85': 'AZ', '86': 'AZ',
            '30': 'GA', '31': 'GA',
            '28': 'NC', '27': 'NC',
            '10': 'NY', '11': 'NY', '12': 'NY', '13': 'NY', '14': 'NY',
            '07': 'NJ', '08': 'NJ',
            '60': 'IL', '61': 'IL', '62': 'IL',
            '48': 'MI', '49': 'MI',
            '43': 'OH', '44': 'OH', '45': 'OH',
            '15': 'PA', '16': 'PA', '17': 'PA', '18': 'PA', '19': 'PA',
            '22': 'VA', '23': 'VA', '24': 'VA',
            '98': 'WA',
            '80': 'CO', '81': 'CO',
            '89': 'NV',
            '84': 'UT',
            '97': 'OR',
            '37': 'TN', '38': 'TN',
            '46': 'IN', '47': 'IN',
            '63': 'MO', '64': 'MO', '65': 'MO',
            '53': 'WI', '54': 'WI',
            '55': 'MN', '56': 'MN',
            '70': 'LA', '71': 'LA',
            '29': 'SC',
            '35': 'AL', '36': 'AL',
            '73': 'OK', '74': 'OK',
            '66': 'KS', '67': 'KS',
            '68': 'NE', '69': 'NE',
            '50': 'IA', '51': 'IA', '52': 'IA',
            '72': 'AR',
            '39': 'MS',
            '20': 'DC', '21': 'MD',
            '06': 'CT',
            '01': 'MA', '02': 'MA',
            '03': 'NH',
            '05': 'VT',
            '04': 'ME',
            '25': 'WV', '26': 'WV',
            '40': 'KY', '41': 'KY', '42': 'KY',
            '57': 'SD', '58': 'ND',
            '59': 'MT',
            '82': 'WY', '83': 'ID',
            '87': 'NM', '88': 'NM',
            '96': 'HI',
            '99': 'AK',
          };

          // Aggregate counts by state code
          const stateAggregation = {};
          Object.entries(data.all_time?.by_state || {}).forEach(([stateCode, count]) => {
            // If it's already a 2-letter state code, use it directly
            // Otherwise, try to map from zip prefix
            const isStateCode = /^[A-Z]{2}$/.test(stateCode);
            const displayState = isStateCode ? stateCode : (ZIP_PREFIX_TO_STATE[stateCode] || stateCode);

            // Aggregate counts for the same state
            if (stateAggregation[displayState]) {
              stateAggregation[displayState] += count;
            } else {
              stateAggregation[displayState] = count;
            }
          });

          // Convert to array and sort by count
          return Object.entries(stateAggregation)
            .map(([state, count]) => ({ state, count }))
            .sort((a, b) => b.count - a.count);
        })(),
      };

      console.log("Transformed Activity Data:", transformedData);
      setActivityData(transformedData);
    } catch (err) {
      console.error("Failed to load marketplace activity:", err);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  // Load archived leads
  const loadArchivedLeads = useCallback(async () => {
    setArchivedLoading(true);
    try {
      const data = await fetchArchivedLeads({ include_team: true });
      setArchivedLeads(data.leads || []);
    } catch (err) {
      console.error("Failed to load archived leads:", err);
    } finally {
      setArchivedLoading(false);
    }
  }, []);

  // Handle archive lead
  const handleArchiveLead = async () => {
    if (!pendingArchiveLead || isArchiving) return;

    // Use custom reason if "Other" selected, otherwise use dropdown value
    const reason = archiveReasonSelect === "Other" ? archiveReasonCustom : archiveReasonSelect;

    setIsArchiving(true);
    try {
      // First, update status to "lost" to keep things consistent
      await updateLeadStatus(pendingArchiveLead.lead_id, "lost", reason);
      // Then archive the lead
      await archiveLead(pendingArchiveLead.lead_id, reason);
      // Remove from active leads
      setLeads((prev) => prev.filter((l) => l.lead_id !== pendingArchiveLead.lead_id));
      // Refresh archived leads count
      loadArchivedLeads();
      // Close modal and reset
      setPendingArchiveLead(null);
      setArchiveReasonSelect("");
      setArchiveReasonCustom("");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsArchiving(false);
    }
  };

  // Handle restore lead
  const handleRestoreLead = async () => {
    if (!pendingRestoreLead || isRestoring) return;

    setIsRestoring(true);
    try {
      await restoreLead(pendingRestoreLead.lead_id);
      // Remove from archived leads
      setArchivedLeads((prev) => prev.filter((l) => l.lead_id !== pendingRestoreLead.lead_id));
      // Refresh active leads
      loadData();
      // Close modal
      setPendingRestoreLead(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRestoring(false);
    }
  };

  // Handle unlock lead from marketplace
  const handleUnlockLead = async (lead, accessCode) => {
    if (isUnlocking) return;

    const requiredCredits = lead.unlock_credits || LEAD_ACCESS_CREDIT_COST;

    // Check credits
    if (userCredits < requiredCredits) {
      setError(`Insufficient credits. You need ${requiredCredits.toLocaleString()} credits but only have ${userCredits.toLocaleString()}.`);
      return;
    }

    // Check access code
    if (!accessCode || accessCode.length !== 8) {
      setError("Please enter a valid 8-character access code");
      return;
    }

    setIsUnlocking(true);
    setError(null);

    try {
      const result = await unlockLead(lead.lead_id, accessCode);

      // Trigger celebration!
      triggerCelebration();

      // Show success modal
      setSuccessModal({
        isOpen: true,
        type: "unlock",
        data: {
          borrower_name: result.lead?.contact_name || result.contact_name,
          email: result.lead?.contact_email || result.contact_email,
          phone: result.lead?.contact_phone || result.contact_phone,
          lead_id: lead.lead_id,
        },
      });

      // Update marketplace to show as unlocked
      setPoolLeads((prev) =>
        prev.map((l) => (l.lead_id === lead.lead_id ? { ...l, is_unlocked: true } : l))
      );

      // Refresh my leads and credits
      loadData();
      if (onCreditsUpdate) {
        onCreditsUpdate();
      }

      // Close the confirm modal
      setPendingUnlockLead(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUnlocking(false);
    }
  };

  // Handle lead row click (My Leads tab)
  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
  };

  // Handle marketplace card click - show unlock confirmation
  const handleMarketplaceCardClick = (lead) => {
    if (lead.is_unlocked) {
      // Already unlocked, switch to My Leads tab
      setActiveTab("myleads");
    } else {
      // Show unlock confirmation
      setPendingUnlockLead(lead);
    }
  };

  // Load leads and stats
  // include_team=true tells backend to return leads accessible to any team member
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [leadsData, statsData] = await Promise.all([
        fetchLeads({ status: statusFilter, limit: 100, include_team: true }),
        fetchLeadStats({ include_team: true }),
      ]);
      setLeads(leadsData.leads || []);
      setStats(statsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  // Handle create lead
  const handleCreateLead = async (leadData) => {
    setIsCreatingLead(true);
    try {
      await createLead(leadData);
      setShowCreateLeadModal(false);
      // Refresh all leads and stats from server
      await loadData();
    } catch (err) {
      console.error("Failed to create lead:", err);
      throw err;
    } finally {
      setIsCreatingLead(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load team context on mount (needed for Daily Reports team_id)
  useEffect(() => {
    loadTeamContext();
  }, [loadTeamContext]);

  // Load marketplace on mount (to show count badge) and when tab changes
  useEffect(() => {
    if (hasMarketplaceAccess && poolLeads.length === 0) {
      loadMarketplace();
    }
  }, [hasMarketplaceAccess, poolLeads.length, loadMarketplace]);

  // Load marketplace activity when switching to leads report tab
  useEffect(() => {
    if (hasMarketplaceAccess && activeTab === "leadsreport" && !activityData) {
      loadActivity();
    }
  }, [hasMarketplaceAccess, activeTab, activityData, loadActivity]);

  // Load archived leads on mount (to show count badge)
  useEffect(() => {
    if (archivedLeads.length === 0) {
      loadArchivedLeads();
    }
  }, [archivedLeads.length, loadArchivedLeads]);

  // Load daily reports when switching to reports tab
  useEffect(() => {
    if (activeTab === "reports" && dailyReports.length === 0 && effectiveTeamId) {
      loadDailyReports();
    }
  }, [activeTab, dailyReports.length, loadDailyReports, effectiveTeamId]);

  // Handle status change
  const handleStatusChange = async (leadId, newStatus, note = "") => {
    try {
      const updatedLead = await updateLeadStatus(leadId, newStatus, note);
      // Preserve notes and ensure status is explicitly set
      setLeads((prev) => prev.map((l) => {
        if (l.lead_id === leadId) {
          return {
            ...l,
            ...updatedLead,
            status: newStatus, // Explicitly set the new status
            notes: updatedLead.notes || l.notes
          };
        }
        return l;
      }));
      if (selectedLead?.lead_id === leadId) {
        setSelectedLead((prev) => ({
          ...prev,
          ...updatedLead,
          status: newStatus, // Explicitly set the new status
          notes: updatedLead.notes || prev.notes
        }));
      }
      // Refresh stats from backend
      const newStats = await fetchLeadStats();
      setStats(newStats);
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle add note (with @mention support - mentioned users will receive email notifications)
  const handleAddNote = async (leadId, note, mentionedUsers = []) => {
    try {
      const updatedLead = await addLeadNote(leadId, note, mentionedUsers);
      setLeads((prev) => prev.map((l) => (l.lead_id === leadId ? updatedLead : l)));
      if (selectedLead?.lead_id === leadId) {
        setSelectedLead(updatedLead);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle update note
  const handleUpdateNote = async (leadId, noteIndex, note) => {
    try {
      const updatedLead = await updateLeadNote(leadId, noteIndex, note);
      setLeads((prev) => prev.map((l) => (l.lead_id === leadId ? updatedLead : l)));
      if (selectedLead?.lead_id === leadId) {
        setSelectedLead(updatedLead);
      }
    } catch (err) {
      setError(err.message);
      throw err; // Re-throw so the panel can handle it
    }
  };

  // Handle delete note
  const handleDeleteNote = async (leadId, noteIndex) => {
    try {
      const updatedLead = await deleteLeadNote(leadId, noteIndex);
      setLeads((prev) => prev.map((l) => (l.lead_id === leadId ? updatedLead : l)));
      if (selectedLead?.lead_id === leadId) {
        setSelectedLead(updatedLead);
      }
    } catch (err) {
      setError(err.message);
      throw err; // Re-throw so the panel can handle it
    }
  };

  // Handle toggle reaction on note (optimistic UI)
  const handleToggleReaction = async (leadId, noteIndex, emoji) => {
    // Helper to toggle reaction in a note
    const toggleReactionInNote = (note, emoji, userEmail) => {
      const reactions = note.reactions || [];
      const existingIdx = reactions.findIndex(r => r.emoji === emoji);

      if (existingIdx >= 0) {
        const existing = reactions[existingIdx];
        const userReacted = existing.users?.includes(userEmail) || existing.userReacted;

        if (userReacted) {
          // Remove user's reaction
          const newCount = (existing.count || 1) - 1;
          if (newCount <= 0) {
            // Remove this reaction entirely
            return reactions.filter((_, i) => i !== existingIdx);
          } else {
            // Decrement count
            return reactions.map((r, i) => i === existingIdx
              ? { ...r, count: newCount, userReacted: false, users: (r.users || []).filter(u => u !== userEmail) }
              : r
            );
          }
        } else {
          // Add user to existing reaction
          return reactions.map((r, i) => i === existingIdx
            ? { ...r, count: (r.count || 1) + 1, userReacted: true, users: [...(r.users || []), userEmail] }
            : r
          );
        }
      } else {
        // Add new reaction
        return [...reactions, { emoji, count: 1, userReacted: true, users: [userEmail] }];
      }
    };

    // Helper to update lead with toggled reaction
    const updateLeadReaction = (lead) => {
      if (lead.lead_id !== leadId) return lead;
      const notes = [...(lead.notes || [])];
      // Find note by its index property (DynamoDB index), not array position
      const noteArrayIdx = notes.findIndex(n => n.index === noteIndex);
      if (noteArrayIdx >= 0) {
        notes[noteArrayIdx] = {
          ...notes[noteArrayIdx],
          reactions: toggleReactionInNote(notes[noteArrayIdx], emoji, userEmail)
        };
      }
      return { ...lead, notes };
    };

    // Optimistic update
    const previousLeads = leads;
    const previousSelectedLead = selectedLead;

    setLeads((prev) => prev.map(updateLeadReaction));
    if (selectedLead?.lead_id === leadId) {
      setSelectedLead(updateLeadReaction(selectedLead));
    }

    // API call
    try {
      const updatedLead = await toggleNoteReaction(leadId, noteIndex, emoji);
      // Use server response if available (it has the authoritative state)
      if (updatedLead && updatedLead.notes) {
        setLeads((prev) => prev.map((l) => (l.lead_id === leadId ? updatedLead : l)));
        if (selectedLead?.lead_id === leadId) {
          setSelectedLead(updatedLead);
        }
      }
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
      // Rollback on error
      setLeads(previousLeads);
      if (previousSelectedLead?.lead_id === leadId) {
        setSelectedLead(previousSelectedLead);
      }
    }
  };

  // Handle assign
  const handleAssign = async (leadId, assignedTo, sendNotification = false) => {
    try {
      const updatedLead = await assignLead(leadId, assignedTo, sendNotification);
      // Preserve notes if API response doesn't include them
      setLeads((prev) => prev.map((l) => {
        if (l.lead_id === leadId) {
          return { ...l, ...updatedLead, notes: updatedLead.notes || l.notes };
        }
        return l;
      }));
      if (selectedLead?.lead_id === leadId) {
        setSelectedLead((prev) => ({ ...prev, ...updatedLead, notes: updatedLead.notes || prev.notes }));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle convert to case
  const handleConvert = async (leadId) => {
    try {
      const result = await convertLeadToCase(leadId);
      // Update lead in list
      const updatedLead = await fetchLead(leadId);
      setLeads((prev) => prev.map((l) => (l.lead_id === leadId ? updatedLead : l)));
      if (selectedLead?.lead_id === leadId) {
        setSelectedLead(null); // Close the detail panel
      }
      // Refresh stats
      const newStats = await fetchLeadStats();
      setStats(newStats);

      // Trigger celebration!
      triggerCelebration();

      // Show beautiful success modal with case number
      setSuccessModal({
        isOpen: true,
        type: "conversion",
        data: {
          case_number: result.case?.caseNumber || result.caseNumber || result.case_number || result.case_id,
          lead_id: leadId,
        },
      });
    } catch (err) {
      setError(err.message);
    }
  };

  // Filter leads by search query, ownership, and status
  const filteredLeads = leads.filter((lead) => {
    // Exclude archived leads from main view (they appear in Archived tab)
    const leadStatus = (lead.status || "").toLowerCase();
    if (leadStatus === "archive") {
      return false;
    }

    // Status filter - filter by selected status card
    if (statusFilter && leadStatus !== statusFilter.toLowerCase()) {
      return false;
    }

    // Ownership filter - check both is_own_lead flag and owner_email comparison
    const isOwnLead = lead.is_own_lead === true ||
      (lead.owner_email && lead.owner_email.toLowerCase() === userEmail?.toLowerCase()) ||
      (lead.assigned_to && lead.assigned_to.toLowerCase() === userEmail?.toLowerCase());

    if (ownershipFilter === "mine" && !isOwnLead) return false;
    if (ownershipFilter === "team" && isOwnLead) return false;

    // Search filter
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      lead.lead_id?.toLowerCase().includes(query) ||
      lead.contact_name?.toLowerCase().includes(query) ||
      lead.contact_email?.toLowerCase().includes(query) ||
      lead.contact_phone?.includes(query) ||
      lead.owner_name?.toLowerCase().includes(query) ||
      lead.property_address?.toLowerCase().includes(query) ||
      lead.property_city?.toLowerCase().includes(query) ||
      lead.property_state?.toLowerCase().includes(query) ||
      lead.property_zip?.includes(query)
    );
  });

  // Sort filtered leads
  const sortedLeads = [...filteredLeads].sort((a, b) => {
    let aVal, bVal;

    switch (sortField) {
      case "contact":
        aVal = (a.contact_name || "").toLowerCase();
        bVal = (b.contact_name || "").toLowerCase();
        break;
      case "phone":
        aVal = a.contact_phone || "";
        bVal = b.contact_phone || "";
        break;
      case "status":
        const statusOrder = { new: 1, contacted: 2, follow_up: 3, qualified: 4, converted: 5, lost: 6 };
        aVal = statusOrder[a.status] || 99;
        bVal = statusOrder[b.status] || 99;
        break;
      case "owner":
        aVal = (a.owner_name || a.assigned_to || "").toLowerCase();
        bVal = (b.owner_name || b.assigned_to || "").toLowerCase();
        break;
      case "source":
        aVal = (a.source || "").toLowerCase();
        bVal = (b.source || "").toLowerCase();
        break;
      case "value":
        aVal = Number(String(a.estimated_value || a.loan_details?.estimatedValue || 0).replace(/[^0-9.-]/g, "")) || 0;
        bVal = Number(String(b.estimated_value || b.loan_details?.estimatedValue || 0).replace(/[^0-9.-]/g, "")) || 0;
        break;
      case "created_at":
      default:
        aVal = new Date(a.created_at || 0).getTime();
        bVal = new Date(b.created_at || 0).getTime();
        break;
    }

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // Handle sort column click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Filter marketplace leads by search query
  const filteredPoolLeads = poolLeads.filter((lead) => {
    if (!marketplaceSearch) return true;
    const query = marketplaceSearch.toLowerCase();
    return (
      lead.lead_id?.toLowerCase().includes(query) ||
      lead.property_city?.toLowerCase().includes(query) ||
      lead.property_state?.toLowerCase().includes(query) ||
      lead.property_zip?.includes(query) ||
      lead.loan_purpose?.toLowerCase().includes(query) ||
      lead.property_type?.toLowerCase().includes(query) ||
      lead.occupancy_type?.toLowerCase().includes(query) ||
      lead.credit_score_range?.toLowerCase().includes(query) ||
      String(lead.estimated_value || "").includes(query) ||
      String(lead.down_payment || "").includes(query) ||
      lead.source?.toLowerCase().includes(query)
    );
  });

  // Filter archived leads by search query
  const filteredArchivedLeads = archivedLeads.filter((lead) => {
    if (!archiveSearch) return true;
    const query = archiveSearch.toLowerCase();
    return (
      lead.lead_id?.toLowerCase().includes(query) ||
      lead.contact_name?.toLowerCase().includes(query) ||
      lead.contact_email?.toLowerCase().includes(query) ||
      lead.contact_phone?.includes(query) ||
      lead.archived_reason?.toLowerCase().includes(query) ||
      lead.property_city?.toLowerCase().includes(query) ||
      lead.property_state?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="h-full flex flex-col bg-[#f7f4ee] dark:bg-slate-950">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Leads Management</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {stats?.total || 0} total leads
            </p>
          </div>

          {/* Pipeline Value Display - Big & Motivational */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-widest">Pipeline Value</p>
              <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-green-400 to-teal-400 drop-shadow-sm">
                {formatCurrency(pipelineStats.activeValue)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Credits Display */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <Coins className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                {userCredits.toLocaleString()} credits
              </span>
            </div>
            {/* Refresh Credits Button */}
            <button
              onClick={handleRefreshCredits}
              disabled={isRefreshingCredits}
              className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              title="Refresh credits"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshingCredits ? "animate-spin" : ""}`} />
            </button>
            {/* Refresh Data Button */}
            <button
              onClick={loadData}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            {/* Create Lead Button */}
            <button
              onClick={() => setShowCreateLeadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg font-medium hover:bg-sky-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Lead
            </button>
          </div>
        </div>

        {/* Tab Buttons - Only show tabs if marketplace access available */}
        {hasMarketplaceAccess && (
          <div className="flex gap-2 mt-4 flex-wrap">
            <button
              onClick={() => {
                setActiveTab("reports");
                setSelectedReport(null);
                setSelectedReportData(null);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === "reports"
                  ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/25"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              <FileBarChart className="w-4 h-4" />
              Daily Reports
              {dailyReports.length > 0 && (
                <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                  activeTab === "reports" ? "bg-white/20" : "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
                }`}>
                  {dailyReports.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("leadsreport")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === "leadsreport"
                  ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/25"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Leads Report
            </button>
            <button
              onClick={() => setActiveTab("myleads")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === "myleads"
                  ? "bg-sky-600 text-white shadow-lg shadow-sky-500/25"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              <Users className="w-4 h-4" />
              My Leads
              {computedStats?.total > 0 && (
                <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                  activeTab === "myleads" ? "bg-white/20" : "bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400"
                }`}>
                  {computedStats.total}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("marketplace")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === "marketplace"
                  ? "bg-violet-600 text-white shadow-lg shadow-violet-500/25"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
              Marketplace
              {poolLeads.length > 0 && (
                <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                  activeTab === "marketplace" ? "bg-white/20" : "bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400"
                }`}>
                  {poolLeads.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("archive")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === "archive"
                  ? "bg-slate-600 text-white shadow-lg shadow-slate-500/25"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              <Archive className="w-4 h-4" />
              Archived
              {archivedLeads.length > 0 && (
                <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                  activeTab === "archive" ? "bg-white/20" : "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400"
                }`}>
                  {archivedLeads.length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Stats Cards - Only show for My Leads tab */}
        {activeTab === "myleads" && computedStats && (
          <div className="flex gap-3 overflow-x-auto pb-2 mt-4">
            <StatsCard
              label="Total"
              value={computedStats.total}
              color="blue"
              icon={Users}
              isActive={statusFilter === null}
              onClick={() => setStatusFilter(null)}
            />
            <StatsCard
              label="New"
              value={computedStats.new}
              color="sky"
              icon={Clock}
              isActive={statusFilter === "new"}
              onClick={() => setStatusFilter("new")}
            />
            <StatsCard
              label="Contacted"
              value={computedStats.contacted}
              color="amber"
              icon={Phone}
              isActive={statusFilter === "contacted"}
              onClick={() => setStatusFilter("contacted")}
            />
            <StatsCard
              label="Processing"
              value={computedStats.processing || 0}
              color="blue"
              icon={Loader2}
              isActive={statusFilter === "processing"}
              onClick={() => setStatusFilter("processing")}
            />
            <StatsCard
              label="Follow-Up"
              value={computedStats.follow_up || 0}
              color="orange"
              icon={RotateCcw}
              isActive={statusFilter === "follow_up"}
              onClick={() => setStatusFilter("follow_up")}
            />
            <StatsCard
              label="Qualified"
              value={computedStats.qualified}
              color="violet"
              icon={Check}
              isActive={statusFilter === "qualified"}
              onClick={() => setStatusFilter("qualified")}
            />
            <StatsCard
              label="Converted"
              value={computedStats.converted}
              color="emerald"
              icon={FileText}
              isActive={statusFilter === "converted"}
              onClick={() => setStatusFilter("converted")}
            />
            <StatsCard
              label="Lost"
              value={computedStats.lost}
              color="slate"
              icon={X}
              isActive={statusFilter === "lost"}
              onClick={() => setStatusFilter("lost")}
            />
          </div>
        )}
      </div>

      {/* Search Bar & Ownership Filter - Only show for My Leads tab */}
      {activeTab === "myleads" && (
        <div className="px-6 py-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            {/* Ownership Filter - Segmented Buttons */}
            <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              <button
                onClick={() => setOwnershipFilter("all")}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                  ownershipFilter === "all"
                    ? "bg-white dark:bg-slate-700 text-sky-600 dark:text-sky-400 shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                All Leads
              </button>
              <button
                onClick={() => setOwnershipFilter("mine")}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                  ownershipFilter === "mine"
                    ? "bg-white dark:bg-slate-700 text-sky-600 dark:text-sky-400 shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                Assigned to Me
              </button>
              <button
                onClick={() => setOwnershipFilter("team")}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                  ownershipFilter === "team"
                    ? "bg-white dark:bg-slate-700 text-sky-600 dark:text-sky-400 shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                Team Leads
              </button>
            </div>
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by ID, name, email, phone, or address..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="px-6 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        {/* DAILY REPORTS TAB */}
        {activeTab === "reports" && (
          <div className={selectedReport ? "h-full" : "p-6"}>
            {/* Reports List View */}
            {!selectedReport && (
              <>
                <div className="flex flex-col gap-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-amber-500" />
                        AI Daily Reports
                      </h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        AI-powered insights and recommendations for your leads pipeline
                      </p>
                    </div>
                    <button
                      onClick={loadDailyReports}
                      disabled={dailyReportsLoading}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      <RefreshCw className={`w-4 h-4 ${dailyReportsLoading ? "animate-spin" : ""}`} />
                      Refresh
                    </button>
                  </div>

                  {/* Time Range Selector */}
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                      {[
                        { key: "daily", label: "Daily", sublabel: "7 days" },
                        { key: "weekly", label: "Weekly", sublabel: "4 weeks" },
                        { key: "monthly", label: "Monthly", sublabel: "3 months" },
                      ].map((range) => (
                        <button
                          key={range.key}
                          onClick={() => {
                            setReportTimeRange(range.key);
                            setShowDatePicker(false);
                          }}
                          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                            reportTimeRange === range.key
                              ? "bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-sm"
                              : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                          }`}
                        >
                          {range.label}
                        </button>
                      ))}
                      <button
                        onClick={() => {
                          setReportTimeRange("custom");
                          setShowDatePicker(true);
                        }}
                        className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                          reportTimeRange === "custom"
                            ? "bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-sm"
                            : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                        }`}
                      >
                        <Calendar className="w-4 h-4" />
                        Custom
                      </button>
                    </div>

                    {/* Custom Date Range Picker */}
                    {reportTimeRange === "custom" && showDatePicker && (
                      <div className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2">
                        <input
                          type="date"
                          value={customDateRange.start}
                          onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                          className="bg-transparent text-sm text-slate-700 dark:text-slate-300 border-none focus:outline-none"
                        />
                        <span className="text-slate-400">to</span>
                        <input
                          type="date"
                          value={customDateRange.end}
                          onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                          className="bg-transparent text-sm text-slate-700 dark:text-slate-300 border-none focus:outline-none"
                        />
                        <button
                          onClick={() => setShowDatePicker(false)}
                          className="ml-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {/* Show current range info */}
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Showing {filteredReports.length} of {dailyReports.length} reports
                    </span>
                  </div>
                </div>

                {dailyReportsLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                  </div>
                ) : dailyReports.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                    <FileBarChart className="w-12 h-12 mb-3 opacity-50" />
                    <p className="text-lg font-medium">No reports yet</p>
                    <p className="text-sm">Daily reports will appear here once generated</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                      Reports are generated daily at 5pm Central
                    </p>
                    <button
                      onClick={() => handleGenerateReport(true)}
                      disabled={generatingReport}
                      className="mt-4 flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-400 text-white font-medium rounded-lg transition-colors"
                    >
                      {generatingReport ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Generate Report Now (500 credits)
                        </>
                      )}
                    </button>
                  </div>
                ) : filteredReports.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                    <Calendar className="w-12 h-12 mb-3 opacity-50" />
                    <p className="text-lg font-medium">No reports in selected range</p>
                    <p className="text-sm">Try adjusting the date range or select a different time period</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Growth Visualization Chart */}
                    {filteredReports.length >= 2 && (() => {
                      // Sort reports by date (oldest first for chart)
                      const sortedReports = [...filteredReports].sort((a, b) =>
                        new Date(a.report_date) - new Date(b.report_date)
                      );

                      // Calculate growth metrics
                      const latestReport = sortedReports[sortedReports.length - 1];
                      const previousReport = sortedReports[sortedReports.length - 2];

                      // Parse values - handle both formatted strings and numbers
                      const parseValue = (val) => {
                        if (typeof val === 'number') return val;
                        if (!val) return 0;
                        return parseFloat(String(val).replace(/[$,]/g, '')) || 0;
                      };

                      const latestLeads = latestReport.active_leads || 0;
                      const previousLeads = previousReport.active_leads || 0;
                      const leadGrowth = latestLeads - previousLeads;
                      const leadGrowthPercent = previousLeads > 0
                        ? ((leadGrowth / previousLeads) * 100).toFixed(1)
                        : 0;

                      const latestValue = parseValue(latestReport.pipeline_value);
                      const previousValue = parseValue(previousReport.pipeline_value);
                      const valueGrowth = latestValue - previousValue;
                      const valueGrowthPercent = previousValue > 0
                        ? ((valueGrowth / previousValue) * 100).toFixed(1)
                        : 0;

                      // Get max values for chart scaling
                      const maxLeads = Math.max(...sortedReports.map(r => r.active_leads || 0));
                      const maxValue = Math.max(...sortedReports.map(r => parseValue(r.pipeline_value)));

                      return (
                        <div className="bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-800 dark:to-slate-900 rounded-2xl p-6 border border-slate-700">
                          {/* Growth Stats Header */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            {/* Lead Count Growth */}
                            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Lead Growth</p>
                              <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold text-white">{latestLeads}</span>
                                <span className={`text-lg font-semibold ${leadGrowth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {leadGrowth >= 0 ? '+' : ''}{leadGrowth}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 mt-1">
                                {leadGrowth >= 0 ? (
                                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                                ) : (
                                  <TrendingDown className="w-4 h-4 text-red-400" />
                                )}
                                <span className={`text-sm font-medium ${leadGrowth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {leadGrowthPercent}% vs yesterday
                                </span>
                              </div>
                            </div>

                            {/* Pipeline Value Growth */}
                            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Pipeline Growth</p>
                              <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold text-emerald-400">
                                  ${(latestValue / 1000000).toFixed(2)}M
                                </span>
                              </div>
                              <div className="flex items-center gap-1 mt-1">
                                {valueGrowth >= 0 ? (
                                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                                ) : (
                                  <TrendingDown className="w-4 h-4 text-red-400" />
                                )}
                                <span className={`text-sm font-medium ${valueGrowth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {valueGrowth >= 0 ? '+' : ''}${(valueGrowth / 1000000).toFixed(2)}M ({valueGrowthPercent}%)
                                </span>
                              </div>
                            </div>

                            {/* Avg Lead Value */}
                            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Avg Lead Value</p>
                              <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold text-violet-400">
                                  {(() => {
                                  const avg = latestLeads > 0 ? latestValue / latestLeads : 0;
                                  if (avg >= 1000000) return `$${(avg / 1000000).toFixed(1)}M`;
                                  if (avg >= 1000) return `$${Math.round(avg / 1000)}K`;
                                  return `$${Math.round(avg)}`;
                                })()}
                                </span>
                              </div>
                              <p className="text-sm text-slate-400 mt-1">
                                per active lead
                              </p>
                            </div>
                          </div>

                          {/* Visual Chart - Bar Chart */}
                          <div className="relative">
                            <p className="text-xs text-slate-400 uppercase tracking-wider mb-3">Daily Trend</p>
                            <div className="flex items-end justify-between gap-2 h-32">
                              {sortedReports.slice(-7).map((report, idx) => {
                                const leads = report.active_leads || 0;
                                const value = parseValue(report.pipeline_value);
                                const heightPercent = maxLeads > 0 ? (leads / maxLeads) * 100 : 0;
                                const isLatest = idx === sortedReports.slice(-7).length - 1;
                                const date = new Date(report.report_date + 'T00:00:00');

                                // Calculate height in pixels (max height is ~100px to leave room for label)
                                const barHeight = maxLeads > 0 ? Math.max((leads / maxLeads) * 100, 8) : 8;

                                return (
                                  <div key={report.report_date} className="flex-1 flex flex-col items-end justify-end gap-1">
                                    <div className="relative w-full flex justify-center">
                                      {/* Value tooltip on hover */}
                                      <div
                                        className={`w-full max-w-12 rounded-t-lg transition-all ${
                                          isLatest
                                            ? 'bg-gradient-to-t from-emerald-600 to-emerald-400'
                                            : 'bg-gradient-to-t from-slate-600 to-slate-500'
                                        }`}
                                        style={{ height: `${barHeight}px` }}
                                        title={`${leads} leads - $${(value / 1000000).toFixed(2)}M`}
                                      >
                                        {isLatest && (
                                          <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap">
                                            <span className="text-xs font-bold text-emerald-400">{leads}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <span className={`text-[10px] ${isLatest ? 'text-white font-semibold' : 'text-slate-500'}`}>
                                      {date.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Growth indicator line */}
                            <div className="absolute bottom-8 left-0 right-0 border-t border-dashed border-slate-600" />
                          </div>
                        </div>
                      );
                    })()}

                    {/* Reports List */}
                    <div className="space-y-3">
                    {filteredReports.map((report) => (
                      <motion.div
                        key={report.report_date}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-lg hover:border-sky-300 dark:hover:border-sky-700 transition-all cursor-pointer"
                        onClick={() => loadReportDetail(report.report_date)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-sky-500 to-blue-600">
                              <FileBarChart className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-900 dark:text-white">
                                {new Date(report.report_date + 'T00:00:00').toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </h3>
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                Generated {new Date(report.generated_at).toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  timeZoneName: 'short'
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                {report.active_leads}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">Active Leads</p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                                {report.pipeline_value}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">Pipeline Value</p>
                            </div>
                            <span className="px-3 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 text-xs font-semibold rounded-full">
                              View Report
                            </span>
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Report Detail View - Sidebar Layout like AI Underwriting Report */}
            {selectedReport && (
              <>
                {reportLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                  </div>
                ) : selectedReportData ? (
                  <ReportDetailView
                    reportData={selectedReportData}
                    teamContext={teamContext}
                    onBack={() => {
                      setSelectedReport(null);
                      setSelectedReportData(null);
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                    <AlertCircle className="w-12 h-12 mb-3 opacity-50" />
                    <p className="text-lg font-medium">Report not found</p>
                    <p className="text-sm">This report may have been removed or is unavailable</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* MY LEADS TAB */}
        {activeTab === "myleads" && (
          <>
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                <Users className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-lg font-medium">No leads found</p>
                <p className="text-sm mb-4">
                  {statusFilter
                    ? `No ${statusFilter} leads`
                    : hasMarketplaceAccess
                      ? "Unlock leads from the marketplace to get started"
                      : "Your unlocked leads will appear here"}
                </p>
                {hasMarketplaceAccess && (
                  <button
                    onClick={() => setActiveTab("marketplace")}
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-colors"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    Browse Marketplace
                  </button>
                )}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0">
                  <tr>
                    {[
                      { field: "contact", label: "Contact" },
                      { field: "phone", label: "Phone" },
                      { field: "status", label: "Status" },
                      { field: "owner", label: "Owner" },
                      { field: "source", label: "Source" },
                      { field: "value", label: "Est. Value" },
                      { field: "created_at", label: "Created" },
                    ].map((col) => (
                      <th
                        key={col.field}
                        onClick={() => handleSort(col.field)}
                        className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors select-none"
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          {sortField === col.field ? (
                            sortDirection === "asc" ? (
                              <ArrowUp className="w-3 h-3" />
                            ) : (
                              <ArrowDown className="w-3 h-3" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-30" />
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {sortedLeads.map((lead) => (
                    <LeadRow
                      key={lead.lead_id}
                      lead={lead}
                      isSelected={selectedLead?.lead_id === lead.lead_id}
                      onClick={() => handleLeadClick(lead)}
                      hasAccess={true}
                      userEmail={userEmail}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* MARKETPLACE TAB - Only available for Business+ users */}
        {activeTab === "marketplace" && hasMarketplaceAccess && (
          <div className="p-6">
            {/* Marketplace Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-violet-500" />
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Available Leads</h2>
                {poolLeads.length > 0 && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400">
                    {poolLeads.length}
                  </span>
                )}
              </div>
              <button
                onClick={loadMarketplace}
                disabled={poolLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${poolLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>

            {/* AVAILABLE LEADS CONTENT */}
            <>
                {/* Marketplace Search Bar */}
                <div className="relative mb-6">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={marketplaceSearch}
                    onChange={(e) => setMarketplaceSearch(e.target.value)}
                    placeholder="Search by location, loan amount, credit score, property type..."
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>

                {/* Marketplace Grid */}
            {poolLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
              </div>
            ) : poolLeads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                <ShoppingBag className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-lg font-medium">No leads available</p>
                <p className="text-sm">Check back later for new opportunities</p>
              </div>
            ) : filteredPoolLeads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                <Search className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-lg font-medium">No matching leads</p>
                <p className="text-sm">Try adjusting your search criteria</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredPoolLeads.map((lead) => (
                  <motion.div
                    key={lead.lead_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-white dark:bg-slate-800 rounded-xl border ${
                      lead.is_unlocked
                        ? "border-emerald-200 dark:border-emerald-800"
                        : "border-slate-200 dark:border-slate-700"
                    } p-4 hover:shadow-lg transition-all cursor-pointer`}
                    onClick={() => handleMarketplaceCardClick(lead)}
                  >
                    {/* Source Badge */}
                    <div className="flex items-center justify-between mb-3">
                      <SourceBadge source={lead.source} />
                      {lead.is_unlocked ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                          <Unlock className="w-3 h-3" />
                          Unlocked
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Lock className="w-3 h-3" />
                        </span>
                      )}
                    </div>

                    {/* Lead Preview Info */}
                    <div className="space-y-2 mb-4">
                      {/* Estimated Value */}
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="text-lg font-bold text-slate-900 dark:text-white">
                          {formatCurrency(lead.estimated_value) || lead.estimated_value_range || "Not specified"}
                        </span>
                      </div>
                      {/* Loan Purpose */}
                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <Home className="w-4 h-4 flex-shrink-0" />
                        <span>{lead.loan_purpose || "Loan"}</span>
                      </div>
                      {/* Location */}
                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span>
                          {[lead.property_city, lead.property_state, lead.property_zip].filter(Boolean).join(", ") || "Location not specified"}
                        </span>
                      </div>
                      {/* Property Type */}
                      {lead.property_type && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Building2 className="w-4 h-4 flex-shrink-0" />
                          <span>{lead.property_type}</span>
                        </div>
                      )}
                      {/* Occupancy Type */}
                      {lead.occupancy_type && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Users className="w-4 h-4 flex-shrink-0" />
                          <span>{lead.occupancy_type}</span>
                        </div>
                      )}
                      {/* Credit Score */}
                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <CreditCard className="w-4 h-4 flex-shrink-0" />
                        <span>Credit: {lead.credit_score || lead.credit_score_range || "N/A"}</span>
                      </div>
                      {/* Down Payment */}
                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <Coins className="w-4 h-4 flex-shrink-0" />
                        <span>Down: {lead.down_payment ? formatCurrency(lead.down_payment) : (lead.down_payment_range || "N/A")}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {lead.is_unlocked ? (
                      <button
                        className="w-full py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg font-medium text-sm hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors"
                      >
                        View in My Leads
                      </button>
                    ) : (
                      <div className="space-y-2">
                        {/* Send to Realtor - Ultra users only */}
                        {hasUltraAccess && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSendToRealtorModal({ isOpen: true, lead });
                            }}
                            className="w-full py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
                          >
                            <Send className="w-4 h-4" />
                            Send to Realtor
                          </button>
                        )}
                        {/* Send to Insurance - Ultra users only */}
                        {hasUltraAccess && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSendToInsuranceModal({ isOpen: true, lead });
                            }}
                            className="w-full py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
                          >
                            <Send className="w-4 h-4" />
                            Send to Insurance
                          </button>
                        )}
                        {/* Unlock Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingUnlockLead(lead);
                          }}
                          disabled={userCredits < (lead.unlock_credits || LEAD_ACCESS_CREDIT_COST)}
                          className="w-full py-2 bg-violet-600 hover:bg-violet-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white disabled:text-slate-500 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
                        >
                          <Coins className="w-4 h-4" />
                          Unlock • {(lead.unlock_credits || LEAD_ACCESS_CREDIT_COST).toLocaleString()} credits
                        </button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
            </>
          </div>
        )}

        {/* LEADS REPORT TAB */}
        {activeTab === "leadsreport" && hasMarketplaceAccess && (
          <div className="p-6">
            {/* Leads Report Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-500" />
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Leads Report</h2>
              </div>
              <button
                onClick={loadActivity}
                disabled={activityLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${activityLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>

            <div className="space-y-6">
              {activityLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                </div>
              ) : activityData ? (
                <>
                  {/* Summary Cards Row */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Today */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Today</span>
                        {activityData.summary?.today?.growth !== undefined && (
                          <span className={`flex items-center gap-0.5 text-xs font-medium ${
                            activityData.summary.today.growth >= 0
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-red-600 dark:text-red-400"
                          }`}>
                            {activityData.summary.today.growth >= 0 ? (
                              <TrendingUp className="w-3 h-3" />
                            ) : (
                              <TrendingDown className="w-3 h-3" />
                            )}
                            {formatGrowthPercent(activityData.summary.today.growth)}
                          </span>
                        )}
                      </div>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        {activityData.summary?.today?.count ?? 0}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">leads acquired</p>
                    </div>

                    {/* This Week */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">This Week</span>
                        {activityData.summary?.this_week?.growth !== undefined && (
                          <span className={`flex items-center gap-0.5 text-xs font-medium ${
                            activityData.summary.this_week.growth >= 0
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-red-600 dark:text-red-400"
                          }`}>
                            {activityData.summary.this_week.growth >= 0 ? (
                              <TrendingUp className="w-3 h-3" />
                            ) : (
                              <TrendingDown className="w-3 h-3" />
                            )}
                            {formatGrowthPercent(activityData.summary.this_week.growth)}
                          </span>
                        )}
                      </div>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        {activityData.summary?.this_week?.count ?? 0}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">leads acquired</p>
                    </div>

                    {/* This Month */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">This Month</span>
                        {activityData.summary?.this_month?.growth !== undefined && (
                          <span className={`flex items-center gap-0.5 text-xs font-medium ${
                            activityData.summary.this_month.growth >= 0
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-red-600 dark:text-red-400"
                          }`}>
                            {activityData.summary.this_month.growth >= 0 ? (
                              <TrendingUp className="w-3 h-3" />
                            ) : (
                              <TrendingDown className="w-3 h-3" />
                            )}
                            {formatGrowthPercent(activityData.summary.this_month.growth)}
                          </span>
                        )}
                      </div>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        {activityData.summary?.this_month?.count ?? 0}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">leads acquired</p>
                    </div>

                    {/* All Time */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">All Time</span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        {activityData.summary?.all_time?.count ?? 0}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">total leads</p>
                    </div>
                  </div>

                  {/* Pipeline Value Cards */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Total Pipeline Value */}
                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 text-white">
                      <div className="flex items-center gap-2 mb-3">
                        <DollarSign className="w-5 h-5 opacity-80" />
                        <span className="text-sm font-medium opacity-90">Total Pipeline Value</span>
                      </div>
                      <p className="text-3xl font-bold">
                        {formatCurrencyAbbreviated(activityData.summary?.all_time?.pipeline_value ?? 0)}
                      </p>
                      <p className="text-sm opacity-75 mt-1">
                        across {activityData.summary?.all_time?.count ?? 0} leads
                      </p>
                    </div>

                    {/* Average Loan Value */}
                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-5 text-white">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-5 h-5 opacity-80" />
                        <span className="text-sm font-medium opacity-90">Avg. Loan Value</span>
                      </div>
                      <p className="text-3xl font-bold">
                        {formatCurrencyAbbreviated(
                          activityData.summary?.all_time?.count > 0
                            ? (activityData.summary.all_time.pipeline_value / activityData.summary.all_time.count)
                            : 0
                        )}
                      </p>
                      <p className="text-sm opacity-75 mt-1">per lead average</p>
                    </div>
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* By Source */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <PieChart className="w-5 h-5 text-indigo-500" />
                        <h3 className="font-semibold text-slate-900 dark:text-white">By Source</h3>
                      </div>
                      {activityData.by_source?.length > 0 ? (
                        <div className="space-y-3">
                          {activityData.by_source.map((item, idx) => {
                            const sourceConfig = SOURCE_CONFIG[item.source?.toLowerCase()] || SOURCE_CONFIG.unknown;
                            const maxCount = Math.max(...activityData.by_source.map(s => s.count));
                            const percentage = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                            return (
                              <div key={item.source || idx}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`text-sm font-medium ${sourceConfig.textClass}`}>
                                    {sourceConfig.label}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-900 dark:text-white">
                                      {item.count}
                                    </span>
                                    <span className="text-xs text-slate-400">
                                      {formatCurrencyAbbreviated(item.pipeline_value)}
                                    </span>
                                  </div>
                                </div>
                                <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all ${
                                      item.source?.toLowerCase() === 'rapidrates' ? 'bg-blue-500' :
                                      item.source?.toLowerCase() === 'nhamy' ? 'bg-emerald-500' :
                                      item.source?.toLowerCase() === 'cloudrealtor' ? 'bg-purple-500' :
                                      'bg-slate-400'
                                    }`}
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
                          No source data available
                        </p>
                      )}
                    </div>

                    {/* By State */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <BarChart3 className="w-5 h-5 text-indigo-500" />
                        <h3 className="font-semibold text-slate-900 dark:text-white">By State (Top 10)</h3>
                      </div>
                      {activityData.by_state?.length > 0 ? (
                        <div className="space-y-3">
                          {activityData.by_state.slice(0, 10).map((item, idx) => {
                            const maxCount = Math.max(...activityData.by_state.map(s => s.count));
                            const percentage = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                            return (
                              <div key={item.state || idx}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    {item.state || "Unknown"}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-900 dark:text-white">
                                      {item.count}
                                    </span>
                                    <span className="text-xs text-slate-400">
                                      {formatCurrencyAbbreviated(item.pipeline_value)}
                                    </span>
                                  </div>
                                </div>
                                <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
                          No state data available
                        </p>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                  <BarChart3 className="w-12 h-12 mb-3 opacity-50" />
                  <p className="text-lg font-medium">No activity data</p>
                  <p className="text-sm">Start acquiring leads to see your report</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ARCHIVE TAB */}
        {activeTab === "archive" && (
          <div className="p-6">
            {/* Archive Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                  Archived Leads
                  {archivedLeads.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
                      {archiveSearch ? `${filteredArchivedLeads.length} of ${archivedLeads.length}` : archivedLeads.length}
                    </span>
                  )}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Leads that are no longer active. Restore them to continue working.
                </p>
              </div>
              <button
                onClick={loadArchivedLeads}
                disabled={archivedLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${archivedLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>

            {/* Archive Search Bar */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={archiveSearch}
                onChange={(e) => setArchiveSearch(e.target.value)}
                placeholder="Search archived leads by name, email, phone, or reason..."
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500"
              />
            </div>

            {/* Archive Grid */}
            {archivedLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-slate-500 animate-spin" />
              </div>
            ) : archivedLeads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                <Archive className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-lg font-medium">No archived leads</p>
                <p className="text-sm">Archived leads will appear here</p>
              </div>
            ) : filteredArchivedLeads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                <Search className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-lg font-medium">No matching archived leads</p>
                <p className="text-sm">Try adjusting your search criteria</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredArchivedLeads.map((lead) => (
                  <motion.div
                    key={lead.lead_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-lg transition-all"
                  >
                    {/* Source Badge & Archive indicator */}
                    <div className="flex items-center justify-between mb-3">
                      <SourceBadge source={lead.source} />
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <Archive className="w-3 h-3" />
                        Archived
                      </span>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="font-semibold text-slate-900 dark:text-white truncate">
                          {lead.contact_name || "Name hidden"}
                        </span>
                      </div>
                      {lead.contact_email && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Mail className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{lead.contact_email}</span>
                        </div>
                      )}
                      {lead.contact_phone && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Phone className="w-4 h-4 flex-shrink-0" />
                          <span>{formatPhoneNumber(lead.contact_phone)}</span>
                        </div>
                      )}
                    </div>

                    {/* Archive Reason */}
                    {lead.archived_reason && (
                      <div className="mb-4 p-2 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-1">Reason:</p>
                        <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-2">{lead.archived_reason}</p>
                      </div>
                    )}

                    {/* Archive Date */}
                    {lead.archived_at && (
                      <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
                        <Calendar className="w-3 h-3" />
                        Archived {formatLeadDate(lead.archived_at)}
                      </div>
                    )}

                    {/* Restore Button */}
                    <button
                      onClick={() => setPendingRestoreLead(lead)}
                      className="w-full py-2 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 rounded-lg font-medium text-sm hover:bg-sky-200 dark:hover:bg-sky-900/50 transition-colors flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Restore Lead
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lead Detail Panel */}
      <AnimatePresence>
        {selectedLead && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setSelectedLead(null)}
            />
            <LeadDetailPanel
              lead={selectedLead}
              onClose={() => setSelectedLead(null)}
              onStatusChange={handleStatusChange}
              onAddNote={handleAddNote}
              onUpdateNote={handleUpdateNote}
              onDeleteNote={handleDeleteNote}
              onToggleReaction={handleToggleReaction}
              onAssign={handleAssign}
              onConvert={handleConvert}
              onArchive={(lead) => {
                setSelectedLead(null);
                setPendingArchiveLead(lead);
              }}
              onSendToRealtor={hasUltraAccess ? (lead) => {
                setSendToRealtorModal({ isOpen: true, lead });
              } : null}
              onSendToInsurance={hasUltraAccess ? (lead) => {
                setSendToInsuranceModal({ isOpen: true, lead });
              } : null}
              teamMembers={teamMembers}
              userEmail={userEmail}
              onStartCall={hasPhoneCallingAccess ? onStartCall : null}
              hasAIDialerAccess={hasAIDialerAccess}
              userCredits={userCredits}
              onRefreshLead={async (leadId) => {
                try {
                  const updatedLead = await fetchLead(leadId);
                  if (updatedLead) {
                    setSelectedLead(updatedLead);
                    // Also update in leads array
                    setLeads(prev => prev.map(l =>
                      (l.lead_id || l.id) === leadId ? updatedLead : l
                    ));
                  }
                } catch (err) {
                  console.error('Failed to refresh lead:', err);
                }
              }}
              onSendSMS={(lead) => {
                setSmsModal({
                  isOpen: true,
                  contact: {
                    phoneNumber: lead.contact_phone,
                    contactName: lead.contact_name,
                    leadId: lead.id
                  }
                });
              }}
            />
          </>
        )}
      </AnimatePresence>

      {/* Unlock Confirmation Modal with Access Code */}
      <AnimatePresence>
        {pendingUnlockLead && (
          <UnlockModal
            lead={pendingUnlockLead}
            userCredits={userCredits}
            creditCost={pendingUnlockLead.unlock_credits || LEAD_ACCESS_CREDIT_COST}
            isUnlocking={isUnlocking}
            onClose={() => setPendingUnlockLead(null)}
            onUnlock={handleUnlockLead}
          />
        )}
      </AnimatePresence>

      {/* Archive Confirmation Modal */}
      <AnimatePresence>
        {pendingArchiveLead && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 max-w-md w-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                  <Archive className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Archive Lead</h3>
                  <p className="text-sm text-slate-500">
                    {pendingArchiveLead.contact_name || "This lead"}
                  </p>
                </div>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Archived leads are removed from your pipeline and won't count toward totals. You can restore them anytime.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Reason for archiving (optional)
                </label>
                <select
                  value={archiveReasonSelect}
                  onChange={(e) => {
                    setArchiveReasonSelect(e.target.value);
                    if (e.target.value !== "Other") {
                      setArchiveReasonCustom("");
                    }
                  }}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-500 mb-2"
                >
                  <option value="">Select a reason...</option>
                  {ARCHIVE_REASONS.map((reason) => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
                {archiveReasonSelect === "Other" && (
                  <input
                    type="text"
                    value={archiveReasonCustom}
                    onChange={(e) => setArchiveReasonCustom(e.target.value)}
                    placeholder="Enter custom reason..."
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setPendingArchiveLead(null);
                    setArchiveReasonSelect("");
                    setArchiveReasonCustom("");
                  }}
                  className="flex-1 px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleArchiveLead}
                  disabled={isArchiving}
                  className="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {isArchiving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Archive className="w-4 h-4" />
                  )}
                  Archive
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Restore Confirmation Modal */}
      <AnimatePresence>
        {pendingRestoreLead && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 max-w-md w-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                  <RotateCcw className="w-6 h-6 text-sky-600 dark:text-sky-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Restore Lead</h3>
                  <p className="text-sm text-slate-500">
                    {pendingRestoreLead.contact_name || "This lead"}
                  </p>
                </div>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                This lead will be restored to your pipeline in the "Contacted" stage. You can continue working on it from there.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setPendingRestoreLead(null)}
                  className="flex-1 px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRestoreLead}
                  disabled={isRestoring}
                  className="flex-1 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {isRestoring ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                  Restore
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sales Celebration Overlay */}
      <SalesCelebrationOverlay state={celebrationState} />

      {/* Success Modal */}
      <AnimatePresence>
        {successModal.isOpen && (
          <SuccessModal
            isOpen={successModal.isOpen}
            type={successModal.type}
            data={successModal.data}
            onClose={() => {
              setSuccessModal({ isOpen: false, type: null, data: null });
              // If it was an unlock, switch to My Leads tab
              if (successModal.type === "unlock") {
                setActiveTab("myleads");
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Send to Realtor Modal */}
      <SendToRealtorModal
        isOpen={sendToRealtorModal.isOpen}
        onClose={() => setSendToRealtorModal({ isOpen: false, lead: null })}
        lead={sendToRealtorModal.lead}
      />

      {/* Send to Insurance Modal */}
      <SendToInsuranceModal
        isOpen={sendToInsuranceModal.isOpen}
        onClose={() => setSendToInsuranceModal({ isOpen: false, lead: null })}
        lead={sendToInsuranceModal.lead}
      />

      {/* SMS Modal - Hidden until 10DLC registration approved
      <SMSModal
        isOpen={smsModal.isOpen}
        onClose={() => setSmsModal({ isOpen: false, contact: null })}
        contact={smsModal.contact}
        agentName={userMeta?.agentName || userMeta?.firstName || 'Your Agent'}
      />
      */}

      {/* Create Lead Modal */}
      <AnimatePresence>
        {showCreateLeadModal && (
          <CreateLeadModal
            isOpen={showCreateLeadModal}
            onClose={() => setShowCreateLeadModal(false)}
            onCreate={handleCreateLead}
            isCreating={isCreatingLead}
          />
        )}
      </AnimatePresence>

    </div>
  );
};

export default LeadsDashboard;
